import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Hotel.css";
import { useAuth } from "../../../AuthProvider/AuthContext";
import Login from "../../Main/Login/Login";
import Signup from "../../Main/Signup/Signup";
import hotelApi, { createHotelQuote, verifyHotelEasebuzzPayment } from "../../../services/hotelSearchApi";
import { saveHotelBooking } from "../Hotels/HotelConfirm/HotelConfirm";
import TravelLoader from "../../Loader/Loader"
import {
  Waves, Wifi, HeartHandshake, Dumbbell, UtensilsCrossed, Martini, ParkingCircle, Snowflake,
  Landmark, Clock3, Smartphone, CreditCard, Search, CalendarDays, ChevronLeft, ChevronRight, Moon, Bed, Users, Tag,
  CheckCircle, ShieldCheck, MessageCircleMore, PartyPopper, Lock, AlertCircle, MapPin,
  BriefcaseBusiness, Mountain,
} from "lucide-react";
import usePageMeta from "../../../hooks/usePageMeta";

const API_BASE =
  (process.env?.REACT_APP_API_URL || "http://localhost:5000").replace(/\/$/, "") + "/api";

// ─── RECEIPT DOWNLOAD ─────────────────────────────────────────
async function downloadHotelReceipt(bookingId, token, reference = "hotel-receipt") {
  const resolvedToken = token || getStoredToken();
  if (!resolvedToken) {
    throw new Error("Please log in to download your hotel receipt.");
  }
  const res = await fetch(`${API_BASE}/hotel-bookings/${bookingId}/receipt`, {
    headers: { Authorization: `Bearer ${resolvedToken}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "Receipt download failed");
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${reference}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ─── STATIC FILTER METADATA ───────────────────────────────────
const sortOptions = [
  { id: "popularity", label: "Popularity" },
  { id: "price_asc", label: "Price: Low → High" },
  { id: "price_desc", label: "Price: High → Low" },
  { id: "rating", label: "Top Rated" },
  { id: "best", label: "Best Value" },
];
const suggestedFilters = [
  { id: "5star", label: "5 Star Hotels" },
  { id: "4star", label: "4 Star Hotels" },
  { id: "3star", label: "3 Star Hotels" },
  { id: "last_minute", label: "Under ₹5,000" },
  { id: "breakfast", label: "Breakfast Available" },
];

function slugify(str) {
  return String(str).toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}
function titleCase(str) {
  return String(str).replace(/\b\w/g, c => c.toUpperCase());
}
function niceStep(rawStep) {
  if (!rawStep || !Number.isFinite(rawStep) || rawStep <= 0) return 500;
  const exp = Math.floor(Math.log10(rawStep));
  const base = rawStep / Math.pow(10, exp);
  const niceBase = base < 1.5 ? 1 : base < 3 ? 2 : base < 7 ? 5 : 10;
  return niceBase * Math.pow(10, exp);
}
function buildPriceRanges(prices, targetBuckets = 6) {
  const values = prices.filter(p => p > 0);
  if (!values.length) return [];
  const min = Math.min(...values), max = Math.max(...values);
  if (min === max) return [{ id: "p1", label: `Around ₹${min.toLocaleString("en-IN")}`, min: 0, max: Infinity }];
  const step = niceStep((max - min) / targetBuckets);
  const start = Math.floor(min / step) * step;
  const buckets = []; let cursor = start, i = 1;
  while (cursor < max) { buckets.push({ id: `p${i}`, min: cursor, max: cursor + step }); cursor += step; i++; }
  buckets[buckets.length - 1].max = Infinity;
  return buckets.map(b => ({
    ...b,
    label: Number.isFinite(b.max)
      ? `₹${b.min.toLocaleString("en-IN")} – ₹${b.max.toLocaleString("en-IN")}`
      : `₹${b.min.toLocaleString("en-IN")}+`,
  }));
}
function buildRatingTiers(ratings) {
  const values = ratings.filter(r => r > 0).sort((a, b) => a - b);
  if (!values.length) return [];
  const pct = (p) => values[Math.min(values.length - 1, Math.floor(p * values.length))];
  const seen = new Set();
  return [0.9, 0.7, 0.4].map(p => Math.round(pct(p) * 10) / 10)
    .filter(v => (seen.has(v) ? false : (seen.add(v), true)))
    .sort((a, b) => b - a)
    .map(min => ({ id: `r${String(min).replace(".", "")}`, label: `${min}+ Rating`, min }));
}
function groupBySingleClient(hotels, getValue, limit = 10) {
  const map = {};
  hotels.forEach(h => { const l = String(getValue(h) || "").trim(); if (l) map[l] = (map[l] || 0) + 1; });
  return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, limit)
    .map(([label, cnt]) => ({ id: slugify(label), label: titleCase(label), count: cnt }));
}
function groupByMultiClient(hotels, getValues, limit = 20) {
  const map = {};
  hotels.forEach(h => (getValues(h) || []).forEach(v => { const l = String(v).trim(); if (l) map[l] = (map[l] || 0) + 1; }));
  return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, limit)
    .map(([label, cnt]) => ({ id: slugify(label), label: titleCase(label), count: cnt }));
}
function extractImageUrl(img) {
  if (typeof img === "string") return img;
  return img?.url || img?.imageUrl || img?.Url || img?.ImageUrl || img?.link || "";
}

function buildImagesByCategory(hotelImagesRaw) {
  const categories = {};
  if (Array.isArray(hotelImagesRaw)) {
    const urls = hotelImagesRaw.map(extractImageUrl).filter(Boolean);
    if (urls.length) categories["Photos"] = urls;
  } else if (hotelImagesRaw && typeof hotelImagesRaw === "object") {
    Object.entries(hotelImagesRaw).forEach(([key, arr]) => {
      if (Array.isArray(arr)) {
        const urls = arr.map(extractImageUrl).filter(Boolean);
        if (urls.length) categories[titleCase(key.replace(/_/g, " "))] = urls;
      }
    });
  }
  return categories;
}
// ─── HELPERS ──────────────────────────────────────────────────
const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const fmtExact = (n) =>
  `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const getStoredToken = () => {
  try {
    return localStorage.getItem("mbt_token");
  } catch {
    return null;
  }
};
const renderStars = (count) => "★".repeat(count) + "☆".repeat(5 - count);
const addDays = (dateStr, days) => {
  const d = new Date(dateStr && dateStr.length >= 8 ? dateStr : Date.now());
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};
const calcNights = (checkIn, checkOut) => {
  try {
    const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000);
    return diff > 0 ? diff : 1;
  } catch { return 1; }
};
const todayStr = () => new Date().toISOString().split("T")[0];
const toNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(String(value).replace(/[^\d.-]/g, ""));
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const toLocalDate = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateIso = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const addMonths = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);

const buildCalendarDays = (monthDate) => {
  const monthStart = startOfMonth(monthDate);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setDate(gridStart.getDate() + index);
    return {
      date: day,
      iso: toDateIso(day),
      inMonth: day.getMonth() === monthDate.getMonth(),
    };
  });
};

const formatDateParts = (value) => {
  const date = toLocalDate(value);
  if (!date) return null;
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
  return { day, month, year, weekday };
};

function HotelDateField({ label, value, min, onChange }) {
  const fieldRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(toLocalDate(value) || toLocalDate(min) || new Date()));
  const parts = formatDateParts(value);
  const minDate = useMemo(() => toLocalDate(min), [min]);
  const todayIso = useMemo(() => toDateIso(startOfDay(new Date())), []);
  const days = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const monthLabel = useMemo(
    () => viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    [viewMonth]
  );

  useEffect(() => {
    const nextValue = toLocalDate(value);
    if (nextValue) setViewMonth(startOfMonth(nextValue));
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event) => {
      if (!fieldRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const isBlocked = (date) => {
    if (!minDate) return false;
    return startOfDay(date).getTime() < startOfDay(minDate).getTime();
  };

  const selectDate = (date) => {
    if (!date || isBlocked(date)) return;
    onChange(toDateIso(date));
    setIsOpen(false);
  };

  return (
    <div className="ht-sb-date-field" style={{ position: "relative" }} ref={fieldRef}>
      <label>{label}</label>
      <button
        type="button"
        className={`ht-sb-date-card${isOpen ? " is-active" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        style={{
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  cursor: "pointer"
}}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="ht-sb-date-card-icon"><CalendarDays size={18} /></span>
            <div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span className="ht-sb-date-card-day" style={{ margin: 0, fontSize: 34 }}>{parts?.day ?? "--"}</span>
                <span className="ht-sb-date-card-meta" style={{ fontSize: 16, fontWeight: 700 }}>{parts ? `${parts.month}'${parts.year}` : "Select date"}</span>
              </div>
              <div className="ht-sb-date-card-weekday" style={{ marginTop: 4, fontSize: 13, color: "#475569" }}>
                {parts?.weekday ?? "Choose a travel date"}
              </div>
            </div>
          </div>

        </div>
      </button>

      {isOpen && (
        <div className="ht-sb-calendar-popover" role="dialog" aria-label={`${label} calendar`}>
          <div className="ht-sb-calendar-header">
            <button type="button" className="ht-sb-calendar-nav" onClick={() => setViewMonth((prev) => addMonths(prev, -1))} aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <div>
              <strong>{monthLabel}</strong>
              <div className="ht-sb-calendar-caption">Select a date</div>
            </div>
            <button type="button" className="ht-sb-calendar-nav" onClick={() => setViewMonth((prev) => addMonths(prev, 1))} aria-label="Next month">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="ht-sb-calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="ht-sb-calendar-grid">
            {days.map(({ date, iso, inMonth }) => {
              const blocked = isBlocked(date);
              const isToday = iso === todayIso;
              const isSelected = value === iso;
              return (
                <button
                  key={iso}
                  type="button"
                  className={`ht-sb-calendar-day${!inMonth ? " is-muted" : ""}${blocked ? " is-disabled" : ""}${isSelected ? " is-selected" : ""}${isToday ? " is-today" : ""}`}
                  onClick={() => selectDate(date)}
                  disabled={blocked}
                  aria-pressed={isSelected}
                  aria-label={date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                >
                  <span>{date.getDate()}</span>
                </button>
              );
            })}
          </div>
          <div className="ht-sb-calendar-footer" style={{ marginTop: 12, display: "flex", justifyContent: "space-between", gap: 8 }}>
            {/* <button type="button" className="ht-sb-calendar-chip" onClick={() => selectDate(new Date())}>Today</button>
            <button type="button" className="ht-sb-calendar-chip ht-sb-calendar-chip--ghost" onClick={() => setIsOpen(false)}>Close</button> */}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── NORMALISE HOTEL ──────────────────────────────────────────
// ✅ Covers every field name variant the supplier might return.
// After first successful search, check console log and tighten these.
function normaliseHotel(raw) {
  const rawAmenities = raw.amenities || raw.Amenities || [];
  // ── Image count — total across every category the supplier returns ──
  const hotelImagesRaw = raw.hotelImages || raw.HotelImages || raw.images || {};
  const totalImageCount = Array.isArray(hotelImagesRaw)
    ? hotelImagesRaw.length
    : Object.values(hotelImagesRaw).reduce(
      (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0
    );
  const imagesByCategory = buildImagesByCategory(hotelImagesRaw);
  // ── Nearest attraction — only if supplier actually returned distances ──
  const nearByAttractionsRaw = raw.nearByAttractions || raw.nearbyAttractions || [];
  const nearestAttraction = Array.isArray(nearByAttractionsRaw) && nearByAttractionsRaw.length > 0
    ? [...nearByAttractionsRaw]
      .filter(a => a?.name && a?.distance != null)
      .sort((a, b) => Number(a.distance) - Number(b.distance))[0] || null
    : null;

  // ── Taxes as a distinct line item (not folded into price) ──
  const taxesAmount = toNumber(
    raw.taxes, raw.tax, raw.taxAmount, raw.tax_amount,
    raw.rooms?.[0]?.rates?.[0]?.totalTax
  );

  // ── Free cancellation — only shown if supplier explicitly says so ──
  const freeCancellation = raw.freeCancellation ?? raw.rooms?.[0]?.rates?.[0]?.refundable ?? raw.refundable ?? null;
  const amenities = rawAmenities.map(a =>
    typeof a === "string" ? a.toLowerCase() :
      (a?.name || a?.label || a?.id || "").toLowerCase()
  ).filter(Boolean);

  // ✅ Normalize houseRules to lowercase string array  
  const rawRules = raw.houseRules || raw.house_rules || [];
  const houseRules = rawRules.map(r =>
    typeof r === "string" ? r.toLowerCase() :
      (r?.name || r?.label || r?.id || "").toLowerCase()
  ).filter(Boolean);

  const price = toNumber(
    raw.rooms?.[0]?.rates?.[0]?.totalRate,
    raw.rooms?.[0]?.rates?.[0]?.baseRate,
    raw.rooms?.[0]?.ratePerNight,
    raw.rooms?.[0]?.rate,
    raw.minRate, raw.min_rate,
    raw.price, raw.Price,
    raw.totalAmount, raw.baseAmount,
    raw.discountedPrice, raw.discounted_price,
    raw.finalPrice, raw.final_price,
    raw.currentPrice, raw.current_price,
    raw.totalPrice, raw.total_price,
    raw.ratePerNight, raw.rate_per_night,
    raw.amount, raw.totalAmount
  );
  const originalPrice = toNumber(
    raw.rooms?.[0]?.originalPrice,
    raw.originalPrice,
    raw.original_price,
    raw.crossedPrice,
    raw.crossed_price,
    raw.slashedPrice,
    raw.strikePrice
  );
  const taxes = toNumber(
    raw.rooms?.[0]?.taxes,
    raw.taxes,
    raw.tax,
    raw.taxAmount,
    raw.tax_amount
  );

  // ── Image — try every possible key ──────────────────────────
  const image =
    raw.mainImage || raw.main_image ||
    raw.imageUrl || raw.image_url ||
    raw.hotelImage || raw.hotel_image ||
    raw.thumbnailUrl || raw.thumbnail ||
    raw.images?.[0]?.url || raw.images?.[0]?.imageUrl || raw.images?.[0] || raw.Images?.[0]?.url || raw.Images?.[0]?.imageUrl || raw.Images?.[0] ||
    raw.photos?.[0]?.url || raw.photos?.[0]?.imageUrl || raw.photos?.[0] || raw.Photos?.[0]?.url || raw.Photos?.[0]?.imageUrl || raw.Photos?.[0] ||
    raw.hotelImages?.[0]?.url || raw.hotelImages?.[0]?.imageUrl || raw.hotelImages?.[0] ||
    raw.roomImage || raw.room_image ||
    "";

  return {
    id: raw.hotelId || raw.hotel_id || raw.id,
    encodedHotelId: raw.hotelId || raw.hotel_id || raw.id,
    name: raw.hotelName || raw.hotel_name || raw.name || "Hotel",
    subtitle: raw.chainName || raw.chain_name || raw.subtitle || "",
    stars: Math.round(Number(raw.starRating || raw.star_rating || raw.stars || 3)),
    location: raw.cityName || raw.city_name || raw.city || raw.location || "",
    locationDetail: raw.address || raw.location_detail || raw.locationDetail || raw.area || "",
    locationId: raw.locationId || raw.location_id || raw.id || raw.code || raw.cityId || raw.city_id || "",
    locationType: raw.type || raw.locationType || raw.location_type || "",
    countryOfResidence: raw.countryOfResidence || "India",
    destinationCountry: raw.destinationCountry || raw.country || raw.countryCode || raw.country_code || "IN",
    checkIn: raw.checkIn || raw.check_in || raw.checkInDate || raw.check_in_date || "",
    checkOut: raw.checkOut || raw.check_out || raw.checkOutDate || raw.check_out_date || "",
    occupancies: (
  Array.isArray(raw.occupancies)
    ? raw.occupancies
    : Array.isArray(raw.occupancy)
      ? raw.occupancy
      : []
).map((o) => ({
  ...o,
  childAges: Array.isArray(o.childAges)
    ? o.childAges
    : [],
})),
    tags: raw.tags || raw.Tags || [],
    highlight: raw.highlight || raw.shortDescription || raw.short_description || raw.tagLine || "",
    ratingLabel: raw.ratingLabel || raw.rating_label || "Good",
    rating: toNumber(raw.userRating, raw.user_rating, raw.guestRating, raw.rating, raw.reviewRating, raw.score),
    ratingCount: toNumber(
      raw.reviewCount,
      raw.review_count,
      raw.ratingCount,
      raw.totalReviews,
      raw.total_reviews,
      raw.reviews,
      raw.reviewNo,
      raw.review_no,
      raw.numberOfReviews,
      raw.number_of_reviews
    ),
    popularity: toNumber(
      raw.reviewCount,
      raw.review_count,
      raw.ratingCount,
      raw.totalReviews,
      raw.total_reviews,
      raw.reviews,
      raw.reviewNo,
      raw.review_no,
      raw.numberOfReviews,
      raw.number_of_reviews
    ),
        price,
originalPrice,
taxes,

// Keep supplier cost separate from the customer-facing payable total.
adminPricing: raw.adminPricing || null,
supplierBookingTotal:
  raw.supplierBookingTotal ?? raw.adminPricing?.supplierTotal ?? null,
bookingTotal: raw.bookingTotal ?? null,
    totalImageCount,
    imagesByCategory,
    nearestAttraction: nearestAttraction
      ? { name: nearestAttraction.name, distance: nearestAttraction.distance, unit: nearestAttraction.unit || "Km" }
      : null,
    freeCancellation,
    discount: raw.discount || raw.discountText || raw.offer_text || "",
    offer: raw.offer || raw.specialOffer || "",
    bankOffer: raw.bankOffer ?? false,
    sponsored: raw.sponsored ?? false,
    propertyType: (raw.propertyType || raw.property_type || raw.accommodationType || raw.type || "hotel").toLowerCase(),
    amenities,
    views: raw.views || [],
    houseRules,
    image,
    rateKey: raw.rooms?.[0]?.rateKey || raw.rateKey || raw.rate_key || "",
    searchKey: raw.searchKey || raw.search_key || "",
    propertyTypeId: slugify(raw.propertyType || raw.property_type || raw.accommodationType || raw.type || "hotel"),
    amenityIds: amenities.map(slugify),
    houseRuleIds: houseRules.map(slugify),
    tagIds: (raw.tags || raw.Tags || []).map(t => slugify(t)),
    offerId: slugify(raw.discount || raw.offer || ""),
    viewIds: (raw.views || raw.roomViews || raw.view || []).map(v => slugify(v)),
    chain: raw.chainName || raw.chain_name || "",
    chainId: slugify(raw.chainName || raw.chain_name || ""),
    locationDetailId: slugify(raw.address || raw.location_detail || raw.locationDetail || raw.area || ""),
  };
}

function extractSuggestions(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data?.autoSuggestions || data?.suggestions || data?.data || data?.results || [];
}

function extractHotels(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  const candidates = [
    data,
    data.data,
    data.Data,
    data.response,
    data.Response,
    data.result,
    data.Result,
    data.results,
    data.Results,
  ].filter(Boolean);
  for (const source of candidates) {
    if (Array.isArray(source)) return source;
    for (const key of [
      "hotels",
      "Hotels",
      "hotelList",
      "HotelList",
      "hotel_list",
      "hotelResults",
      "HotelResults",
      "results",
      "Results",
      "data",
      "Data",
    ]) {
      if (Array.isArray(source?.[key])) return source[key];
    }
  }
  return [];
}

function extractSearchKey(data) {
  const candidates = [
    data,
    data?.data,
    data?.Data,
    data?.response,
    data?.Response,
    data?.result,
    data?.Result,
  ].filter(Boolean);
  for (const source of candidates) {
    const key = source.searchKey || source.search_key || source.SearchKey || source.sessionId || source.searchId;
    if (key) return key;
  }
  return "";
}

function pickBestLocationSuggestion(list, query = "") {
  if (!Array.isArray(list) || list.length === 0) return null;

  const q = String(query || "").trim().toLowerCase();
  const itemLabel = (item) => String(
    item?.name ||
    item?.cityName ||
    item?.city_name ||
    item?.label ||
    item?.hotelName ||
    item?.hotel_name ||
    ""
  ).trim().toLowerCase();

  if (q) {
    const exactHotel = list.find(
      (item) =>
        String(item?.type || "").toLowerCase() === "hotel" &&
        itemLabel(item) === q
    );

    if (exactHotel) return exactHotel;

    const exact = list.find(
      (item) => itemLabel(item) === q
    );

    if (exact) return exact;

    const startsWith = list.find(
      (item) =>
        itemLabel(item).startsWith(q) &&
        String(item?.type || "").toLowerCase() !== "state"
    );

    if (startsWith) return startsWith;
  }

  return list.find(item => item?.type && item.type !== "State") || list[0];
}

function isRelatedLocationSuggestion(item, query = "") {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const label = String(
    item?.name ||
    item?.cityName ||
    item?.city_name ||
    item?.label ||
    item?.hotelName ||
    item?.hotel_name ||
    ""
  ).trim().toLowerCase();
  return label === q || label.startsWith(q) || q.startsWith(label);
}

function isIndiaCountry(value) {
  return /^(in|india)$/i.test(String(value || "").trim());
}
const MEAL_KEYWORDS = {
  breakfast: ["breakfast", "bed and breakfast", "b&b", "bb rate"],
  lunch: ["lunch", "half board", "halfboard", "hb rate"],
  dinner: ["dinner", "half board", "halfboard", "hb rate", "full board", "fullboard", "fb rate", "all inclusive", "ai rate"],
};

const getSupplierHotelId = (hotelLike = {}) =>
  hotelLike.hotelId ||
  hotelLike.hotel_id ||
  hotelLike.encodedHotelId ||
  hotelLike.id ||
  "";

const getSupplierRoomId = (room = {}) =>
  room.id ||
  room.roomId ||
  room.room_id ||
  "";

const getSupplierBookingRoomId = (room = {}, rate = {}) =>
  rate.roomId ||
  rate.room_id ||
  getSupplierRoomId(room);

const getSupplierRateId = (rate = {}) =>
  rate.id ||
  rate.rateId ||
  rate.rate_id ||
  rate.rateKey ||
  rate.rate_key ||
  "";

const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const normalizeOccupancyRoom = (room = {}) => {
  const childAges = Array.isArray(room.childAges)
    ? room.childAges.map(Number)
    : [];
  const childCount = Number(room.childCount ?? childAges.length ?? 0) || 0;
  return {
    adultCount: Number(room.adultCount || 0),
    childCount,
    childAges: childAges.slice(0, childCount),
  };
};

function getIncludedMealsFromRate(rate) {
  if (!rate) return [];
  const haystack = [
    rate.name,
    rate.type,
    ...(Array.isArray(rate.roomFacilities) ? rate.roomFacilities : []),
  ].filter(Boolean).join(" ").toLowerCase();

  return Object.entries(MEAL_KEYWORDS)
    .filter(([, keywords]) => keywords.some(k => haystack.includes(k)))
    .map(([meal]) => meal);
}
function getMealChargeFromRate(rate, mealKey) {
  if (!rate || !Array.isArray(rate.additionalCharges)) return null;
  const keywords = MEAL_KEYWORDS[mealKey] || [mealKey];

  const match = rate.additionalCharges.find((charge) => {
    const haystack = [charge.name, charge.type, charge.label, charge.description]
      .filter(Boolean).join(" ").toLowerCase();
    return keywords.some(k => haystack.includes(k));
  });
  if (!match) return null;

  const amount = toNumber(
    match.amount, match.price, match.total, match.value,
    match.chargeAmount, match.perPersonAmount, match.rate
  );
  if (!amount) return null;

  const basisRaw = String(match.chargeType || match.frequency || match.basis || "").toLowerCase();
  return {
    amount,
    perNight: basisRaw.includes("night"),
    perPerson: basisRaw.includes("person") || basisRaw.includes("guest") || basisRaw.includes("pax"),
    label: match.name || match.label || null,
  };
}
function pickDefaultRoomAndRate(rooms, preferBreakfast) {
  let best = null; // { roomKey, rateIndex, hasBreakfast }
  rooms.forEach((room) => {
    const roomKey = room.id || room.name;
    (room.rates || []).forEach((rate, rateIndex) => {
      const available =
        rate.noOfRoomsLeft === undefined ||
        rate.noOfRoomsLeft === null ||
        rate.noOfRoomsLeft === "" ||
        Number(rate.noOfRoomsLeft) > 0;
      if (!available) return;
      const hasBreakfast = preferBreakfast && getIncludedMealsFromRate(rate).includes("breakfast");
      // Prefer: first breakfast-inclusive available rate found.
      // Fall back to: first available rate found, if no breakfast rate exists.
      if (hasBreakfast && (!best || !best.hasBreakfast)) {
        best = { roomKey, rateIndex, hasBreakfast: true };
      } else if (!best) {
        best = { roomKey, rateIndex, hasBreakfast: false };
      }
    });
  });
  return best;
}

function EasyBuzzPayment({ bookingData, amount, token, onSuccess, onFailure, onClose, onAuthExpired }) {
  const [status, setStatus] = useState("idle");
  const [txnData, setTxnData] = useState(null);
  const [error, setError] = useState("");
  const paymentClickRef = useRef(false);

  const resolvedToken = token || getStoredToken();

  useEffect(() => {
    if (!resolvedToken) {
      setStatus("failed");
      setError("Please log in to continue payment.");
      onAuthExpired?.();
    }
  }, [resolvedToken]);

  const initiatePayment = async () => {
    setStatus("initiating");
setError("");

const paymentAmount = Number(amount);

if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
  setStatus("failed");
  setError(
    "The booking amount is unavailable. Please go back and reselect the room."
  );
  return null;
}

try {
      const res = await fetch(`${API_BASE}/payment/easebuzz/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${resolvedToken}` },
        body: JSON.stringify({
          service: "hotel", // server-priced hotel branch (amount comes from the server)
          amount: paymentAmount.toFixed(2),
          productinfo: `Hotel stay at ${bookingData.hotelName}`,
          firstname: bookingData.guestFirstName || "Traveller",
          email: bookingData.guestEmail || "",
          phone: bookingData.guestPhone || "9999999999",
          bookingReference: bookingData.bookingReference || `HOTEL-${Date.now()}`,
          udf1: bookingData.checkIn || "",
          udf2: bookingData.hotelLocation || "",
          udf3: String(bookingData.hotelStars || ""),
        }),
      });

      if (res.status === 401) {
        onAuthExpired?.();
        throw new Error("Your login session expired. Please log in again to continue payment.");
      }

      const data = await res.json();
      if (!res.ok || !data.access_key) throw new Error(data.error || "Payment initiation failed.");

      setTxnData(data);
      setStatus("ready");
      return data;
    } catch (err) {
      setError(err.message || "Could not initiate payment. Please try again.");
      setStatus("failed");
      return null;
    }
  };

  const handlePay = async () => {
  if (paymentClickRef.current) return;

  paymentClickRef.current = true;
  setError("");

  if (!resolvedToken) {
    paymentClickRef.current = false;
    setStatus("failed");
    setError("Please log in to continue payment.");
    onAuthExpired?.();
    return;
  }

  const paymentAmount = Number(amount);

  if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
    paymentClickRef.current = false;
    setStatus("failed");
    setError(
      "The booking amount is unavailable. Please go back and reselect the room."
    );
    return;
  }

  const paymentSession = txnData?.access_key ? txnData : await initiatePayment();

  if (!paymentSession?.access_key) {
    paymentClickRef.current = false;
    return;
  }

  setStatus("processing");

  const openEasebuzz = () => {
    try {
      console.log("[Easebuzz Frontend] Opening checkout:", {
        env: paymentSession.env,
        hasKey: Boolean(paymentSession.key),
        hasAccessKey: Boolean(paymentSession.access_key),
      });

      if (typeof window.EasebuzzCheckout !== "function") {
        throw new Error("Easebuzz checkout SDK did not load correctly.");
      }

      const easebuzzCheckout = new window.EasebuzzCheckout(
        paymentSession.key,
        paymentSession.env || "test"
      );

      const options = {
        access_key: paymentSession.access_key,

        onResponse: async (response) => {
          console.log("[Easebuzz Frontend] Payment response:", response);

          if (String(response?.status || "").toLowerCase() === "success") {
            setStatus("confirming");

            try {
              await onSuccess(response);
            } catch (err) {
              console.error(
                "[Easebuzz Frontend] Post-payment confirmation failed:",
                err
              );

              paymentClickRef.current = false;
              setStatus("failed");
              setError(
                err?.message ||
                  "Payment succeeded but booking confirmation failed."
              );
            }

            return;
          }

          paymentClickRef.current = false;
          setStatus("failed");

          const message =
            response?.error_Message ||
            response?.error ||
            response?.message ||
            "Payment was not completed.";

          setError(`Payment ${response?.status || "failed"}: ${message}`);

          onFailure?.(response);
        },

        theme: "#10b981",
      };

      console.log("[Easebuzz Frontend] Calling initiatePayment");

      easebuzzCheckout.initiatePayment(options);
    } catch (err) {
      console.error("[Easebuzz Frontend] Checkout launch failed:", err);

      paymentClickRef.current = false;
      setStatus("failed");
      setError(
        err?.message ||
          "Could not open the payment gateway. Please try again."
      );
    }
  };

  // SDK is already available — don't inject it again.
  if (typeof window.EasebuzzCheckout === "function") {
    openEasebuzz();
    return;
  }

  const existingScript = document.querySelector(
    'script[src="https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js"]'
  );

  if (existingScript) {
    existingScript.addEventListener("load", openEasebuzz, { once: true });

    existingScript.addEventListener(
      "error",
      () => {
        paymentClickRef.current = false;
        setStatus("failed");
        setError("Could not load the payment gateway. Please try again.");
      },
      { once: true }
    );

    return;
  }

  const script = document.createElement("script");

  script.src =
    "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js";

  script.async = true;

  script.onload = openEasebuzz;

  script.onerror = () => {
    console.error("[Easebuzz Frontend] Checkout SDK failed to load.");

    paymentClickRef.current = false;
    setStatus("failed");
    setError("Could not load the payment gateway. Please try again.");
  };

  document.body.appendChild(script);
};

  return (
    <div className="ht-bm-payment-wrapper">
      <div className="easebuzz-amount-bar">
        <div>
          <div className="easebuzz-amount-label">Total Amount</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
            {bookingData.hotelName} · {bookingData.hotelLocation}
          </div>
        </div>
        <div className="easebuzz-amount-value">{fmt(amount)}</div>
      </div>

      {(
        status === "initiating" ||
        status === "processing" ||
        status === "confirming"
      ) && (
          <div className="easebuzz-loading">
            <TravelLoader inline />
            <div style={{ fontSize: 13, color: "#64748b" }}>
              {status === "initiating"
                ? "Securing your payment session…"
                : status === "processing"
                  ? "Opening payment gateway…"
                  : "Payment successful. Confirming your booking…"}
            </div>
          </div>
        )}

      {(status === "idle" || status === "ready") && (
        <div className="easebuzz-container">
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1b3e", marginBottom: 4 }}>Secure Checkout via EasyBuzz</div>
            <div style={{ fontSize: 11.5, color: "#64748b" }}>All major cards, UPI, net banking & wallets accepted.</div>
          </div>
          <div style={{ background: "#fff", borderRadius: 10, padding: "12px 14px", marginBottom: 14, border: "1px solid #e2e8f0" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
              {[
                { label: "Hotel", value: bookingData.hotelName },
                { label: "Location", value: bookingData.hotelLocation },
                { label: "Check in", value: bookingData.checkIn },
                { label: "Nights", value: `${bookingData.nights} night${bookingData.nights !== 1 ? "s" : ""}` },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: 10, color: "#5e5e5e", fontWeight: 600, textTransform: "uppercase" }}>{label}</div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "#0d1b3e", marginTop: 1 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="easebuzz-pay-btn-wrapper">

            <button
  type="button"
  onClick={handlePay}
  className="ht-bm-btn-primary"
  style={{ width: "100%", marginTop: 8 }}
>
              Pay {fmt(amount)} Securely
            </button>
            <button type="button" className="ht-bm-btn-back" style={{ marginTop: 10, width: "100%" }} onClick={onClose}>Cancel</button>
          </div>
          <div className="payment-security-badge"><ShieldCheck size={15} /> 256-bit SSL encryption · PCI-DSS compliant · Powered by EasyBuzz</div>

        </div>
      )}

      {status === "failed" && (
        <div style={{ padding: "20px 0" }}>
          <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 12, padding: "16px 20px", marginBottom: 16, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>❌</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#b91c1c", marginBottom: 4 }}>Payment Failed</div>
            <div style={{ fontSize: 12.5, color: "#64748b" }}>{error}</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={handlePay} className="ht-bm-btn-primary" style={{ flex: 1 }}>Pay {fmt(amount)} Securely</button>
            <button onClick={onClose} className="ht-bm-btn-back" style={{ flex: 1 }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── SPECIAL REQUEST OPTIONS ────────────────────────────────
const SPECIAL_REQUEST_GROUPS = [
  {
    key: "roomPreference", title: "Room Preference", type: "multi", options: [
      { id: "lift_lobby", label: "Room near lift lobby" },
      { id: "higher_floor", label: "Room on a higher floor" },
    ]
  },
  {
    key: "smokingPreference", title: "Smoking Preference", type: "single", options: [
      { id: "smoking", label: "Smoking Room" },
      { id: "non_smoking", label: "Non-Smoking Room" },
    ]
  },
  {
    key: "transfers", title: "Transfers", type: "single", options: [
      { id: "airport", label: "Airport Transfers" },
      { id: "railway", label: "Railway Transfers" },
      { id: "bus", label: "Bus Station Transfers" },
    ]
  },
  {
    key: "celebrations", title: "Celebrations", type: "multi", options: [
      { id: "cake", label: "Celebration cake" },
      { id: "decoration", label: "Room decoration" },
    ]
  },
];
function buildSpecialRequestsItems({ specialRequests, requestedMealNames, freeText, mealAddOnItems = [] }) {
  const items = [];

  SPECIAL_REQUEST_GROUPS.forEach((group) => {
    const selection = specialRequests[group.key];
    if (group.type === "multi") {
      (selection || []).forEach((id) => {
        const opt = group.options.find((o) => o.id === id);
        if (opt) items.push({ category: group.key, code: opt.id, label: opt.label });
      });
    } else if (selection) {
      const opt = group.options.find((o) => o.id === selection);
      if (opt) items.push({ category: group.key, code: opt.id, label: opt.label });
    }
  });

  requestedMealNames.forEach((label) => {
    const key = label.toLowerCase();
    const info = mealAddOnItems.find(i => i.meal === key);
    const priceNote = info?.priced
      ? ` — ₹${info.amount.toLocaleString("en-IN")} add-on`
      : " (pay at property, add-on request)";
    items.push({ category: "meal", code: key, label: `${label}${priceNote}` });
  });

  if (freeText?.trim()) {
    items.push({ category: "note", code: "custom", label: freeText.trim() });
  }

  return items;
}
function SpecialRequestsModal({ initial, onClose, onSave, saving }) {
  const [selections, setSelections] = useState(initial || {});

  const toggleMulti = (groupKey, id) => {
    setSelections(prev => {
      const cur = prev[groupKey] || [];
      const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
      return { ...prev, [groupKey]: next };
    });
  };
  const setSingle = (groupKey, id) => {
    setSelections(prev => ({ ...prev, [groupKey]: prev[groupKey] === id ? null : id }));
  };

  const totalSelected = SPECIAL_REQUEST_GROUPS.reduce((sum, g) => {
    const v = selections[g.key];
    return sum + (g.type === "multi" ? (v || []).length : v ? 1 : 0);
  }, 0);

  return (
    <div className="ht-bm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="ht-bm-modal" style={{ maxWidth: 520 }}>
        <div className="ht-bm-header">
          <div>
            <div className="ht-bm-title">Special Requests</div>
            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
              These are requests only — the hotel confirms availability at check-in.
            </div>
          </div>
          <button className="ht-bm-close" onClick={onClose}>✕</button>
        </div>

        <div className="ht-bm-body" style={{ padding: "18px 24px" }}>
          {SPECIAL_REQUEST_GROUPS.map((group, idx) => (
            <div
              key={group.key}
              style={{
                marginBottom: 18,
                paddingBottom: idx < SPECIAL_REQUEST_GROUPS.length - 1 ? 18 : 0,
                borderBottom: idx < SPECIAL_REQUEST_GROUPS.length - 1 ? "1px solid #f1f5f9" : "none",
              }}
            >
              <div style={{
                fontWeight: 700, fontSize: 12.5, color: "#0d1b3e",
                marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.3,
              }}>
                {group.title}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {group.options.map(opt => {
                  const active = group.type === "multi"
                    ? (selections[group.key] || []).includes(opt.id)
                    : selections[group.key] === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() =>
                        group.type === "multi"
                          ? toggleMulti(group.key, opt.id)
                          : setSingle(group.key, opt.id)
                      }
                      style={{
                        padding: "8px 14px",
                        borderRadius: 10,
                        border: active ? "2px solid #0f766e" : "1px solid #cbd5e1",
                        background: active ? "#ecfdf5" : "#fff",
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: active ? "#0f766e" : "#334155",
                        cursor: "pointer",
                      }}
                    >
                      {active ? "✓ " : ""}{opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "0 24px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span style={{ fontSize: 11.5, color: "#64748b" }}>
            {totalSelected > 0 ? `${totalSelected} request${totalSelected !== 1 ? "s" : ""} selected` : "No requests selected"}
          </span>
          <button
            className="ht-bm-btn-primary"
            style={{ minWidth: 140 }}
            disabled={saving}
            onClick={() => onSave(selections)}
          >
            {saving ? "Saving…" : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BOOKING MODAL ────────────────────────────────────────────
export function BookingModal({ hotel, searchMeta, onClose, token, user }) {
  const { expireSession } = useAuth();
  const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const navigate = useNavigate();
  const [confirmedRef, setConfirmedRef] = useState("");
  const [confirmedBookingId, setConfirmedBookingId] = useState(null);
  const [step, setStep] = useState(1);
  const [payMethod, setPayMethod] = useState("card");
  const [processing, setProcessing] = useState(false);
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);
  const [paymentPurpose, setPaymentPurpose] = useState("booking");
  const [paymentError, setPaymentError] = useState("");
  const [checkoutPayload, setCheckoutPayload] = useState(null);
  const [liveHotelId, setLiveHotelId] = useState(hotel.id);
  const [holdAllowed, setHoldAllowed] = useState(null);
  const [bookingCheckIn, setBookingCheckIn] = useState(searchMeta.checkIn || todayStr());
  const [bookingCheckOut, setBookingCheckOut] = useState(searchMeta.checkOut || addDays(todayStr(), 1));
  const [bookingRooms, setBookingRooms] = useState(searchMeta.rooms || searchMeta.occupancies?.length || 1);
  const [liveSearchKey, setLiveSearchKey] = useState(hotel.searchKey || searchMeta.searchKey || "");
  const readRoomAvailability = (rate) => {
    const raw = rate?.noOfRoomsLeft ?? rate?.roomsLeft ?? rate?.availableRooms;
    if (raw === undefined || raw === null || raw === "") return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // ── Multi-room selection state ──────────────────────────────
  const [selectedRateIndexByRoom, setSelectedRateIndexByRoom] = useState({});
  const [roomQuantities, setRoomQuantities] = useState({}); // { roomKey: qty }
  const [fetchedRooms, setFetchedRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [roomsError, setRoomsError] = useState("");
  const isFetchingRef = useRef(false);
const initialRoomFetchKeyRef = useRef("");
const bookingRequestIdRef = useRef(
  searchMeta.requestId || generateRequestId()
);
const roomFetchSeqRef = useRef(0);
const checkoutSelectionRef = useRef(null);
const holdConfirmInFlightRef = useRef(false);
  const fetchRoomAvailability = useCallback(async (options = {}) => {
    const preserveSelection = options.preserveSelection === true;
    if (!hotel.id) return null;
    if (isFetchingRef.current) return null;
    isFetchingRef.current = true;

    const mySeq = ++roomFetchSeqRef.current;
    setRoomsLoading(true);
    setRoomsError("");
    const requestedRoomCount =
      Number(searchMeta.rooms) ||
      Number(searchMeta.occupancies?.length) ||
      1;

    const liveOccupancies =
      resolveBookingOccupancies(requestedRoomCount);

    if (!preserveSelection) {
      setRoomsLoaded(false);
      setFetchedRooms([]);
      setRoomQuantities({});
    }

    try {
      // ✅ STEP 1: Get a BRAND NEW ticket from /search — the old one may be expired
      let freshSearchKey = "";
let matchedHotel = null;

const findSameHotel = (hotelList = []) => {
  const selectedHotelId = String(
    getSupplierHotelId(hotel) || ""
  ).trim();

  const selectedHotelName = String(
    hotel.name || hotel.hotelName || ""
  ).trim().toLowerCase();

  // Best/safest match: supplier hotel ID.
  const idMatch = hotelList.find(
    (item) =>
      selectedHotelId &&
      String(getSupplierHotelId(item) || "").trim() ===
        selectedHotelId
  );

  if (idMatch) return idMatch;

  // Safe fallback for a refreshed supplier session.
  const exactNameMatches = hotelList.filter(
    (item) =>
      selectedHotelName &&
      String(
        item.name || item.hotelName || item.hotel_name || ""
      ).trim().toLowerCase() === selectedHotelName
  );

  // Never guess if more than one hotel has the same name.
  return exactNameMatches.length === 1
    ? exactNameMatches[0]
    : null;
};

if (searchMeta.locationId) {
        try {
          const searchRes = await hotelApi.search({
            locationId: searchMeta.locationId,
            checkIn: bookingCheckIn,
            checkOut: bookingCheckOut,
            occupancies: liveOccupancies,
            countryOfResidence: searchMeta.countryOfResidence || "India",
            travelType: searchMeta.travelType ?? 1,
            requestId: bookingRequestIdRef.current,
          });
          freshSearchKey =
  searchRes?.searchKey ||
  searchRes?.search_key ||
  freshSearchKey;

const freshHotelList = searchRes?.hotels || [];

matchedHotel = findSameHotel(freshHotelList);
        } catch (err) {
          console.error(
            "[BookingModal] Fresh hotel search failed:",
            err.message || err
          );

          throw new Error(
            "Live hotel availability could not be refreshed. Please try again before payment."
          );
        }
      }

      // The refreshed city search may not contain the selected property in its
// current result page even though the property is still bookable.
// Resolve that exact hotel once through Autosuggest, then perform ONE
// supplier Search Init using the property's own locationId.
if (
  searchMeta.locationId &&
  !matchedHotel &&
  String(hotel.name || "").trim()
) {
  try {
    const autoResponse = await hotelApi.autosuggest(
      String(hotel.name).trim()
    );

    const suggestions = extractSuggestions(autoResponse);

    const selectedHotelName = String(
      hotel.name || ""
    ).trim().toLowerCase();

    const exactHotelSuggestion = suggestions.find((item) => {
      const type = String(
        item?.type ||
        item?.locationType ||
        item?.location_type ||
        ""
      ).trim().toLowerCase();

      const name = String(
        item?.name ||
        item?.hotelName ||
        item?.hotel_name ||
        item?.label ||
        ""
      ).trim().toLowerCase();

      return type === "hotel" && name === selectedHotelName;
    });

    const exactHotelLocationId =
      exactHotelSuggestion?.locationId ||
      exactHotelSuggestion?.location_id ||
      exactHotelSuggestion?.id ||
      exactHotelSuggestion?.code ||
      "";

    if (exactHotelLocationId) {
      console.log(
        "[CHECKOUT HOTEL RECOVERY] Retrying fresh search with exact hotel",
        {
          hotelName: hotel.name,
          locationId: exactHotelLocationId,
        }
      );

      const exactHotelSearch = await hotelApi.search({
        locationId: exactHotelLocationId,
        checkIn: bookingCheckIn,
        checkOut: bookingCheckOut,
        occupancies: liveOccupancies,
        countryOfResidence:
          searchMeta.countryOfResidence || "India",
        travelType: searchMeta.travelType ?? 1,
        requestId: bookingRequestIdRef.current,
      });

      const exactSearchKey =
        exactHotelSearch?.searchKey ||
        exactHotelSearch?.search_key ||
        "";

      const exactHotelList =
        exactHotelSearch?.hotels || [];

      const recoveredHotel =
        findSameHotel(exactHotelList);

      if (exactSearchKey && recoveredHotel) {
        freshSearchKey = exactSearchKey;
        matchedHotel = recoveredHotel;

        console.log(
          "[CHECKOUT HOTEL RECOVERY] Exact hotel recovered",
          {
            searchKey: freshSearchKey,
            hotelId: getSupplierHotelId(matchedHotel),
            hotelName:
              matchedHotel.name ||
              matchedHotel.hotelName ||
              hotel.name,
          }
        );
      }
    }
  } catch (recoveryErr) {
    console.warn(
      "[CHECKOUT HOTEL RECOVERY] Exact-hotel recovery failed:",
      recoveryErr?.message || recoveryErr
    );
  }
}

      // ✅ Guard: fresh search found no matching hotel — the old hotelId and
// new searchKey are NOT a valid pair.
if (searchMeta.locationId && !matchedHotel) {
  if (mySeq !== roomFetchSeqRef.current) return null;

  if (!preserveSelection) {
    setRoomsError(
      "This hotel is no longer available for the selected dates. Please try different dates or another hotel."
    );
    setFetchedRooms([]);
  }

  throw new Error(
    "This hotel could not be confirmed for the selected dates."
  );
}

      // ✅ STEP 2: Use that NEW ticket to fetch rooms/rates
     // ✅ STEP 2: Use the fresh ticket to fetch rooms/rates.
// If this supplier session contains broken inventory data, create ONE
// completely new search session automatically and retry Details with it.
const buildDetailsRequest = () => ({
  hotelId: getSupplierHotelId(matchedHotel),
  searchKey: freshSearchKey,
  checkIn: bookingCheckIn,
  checkOut: bookingCheckOut,
  occupancies: liveOccupancies.map((o) => ({
    ...o,
    title: o.title || "Mr",
    childAges: Array.isArray(o.childAges)
      ? o.childAges
      : [],
  })),
  countryOfResidence: searchMeta.countryOfResidence || "India",
  travelType: searchMeta.travelType ?? 1,
  requestId: bookingRequestIdRef.current,
});

let data;

try {
  data = await hotelApi.details(buildDetailsRequest());
} catch (err) {
  const supplierError = String(
    err?.details || err?.message || ""
  );

  const isBrokenSupplierSession =
    Number(err?.status) === 500 &&
    supplierError.includes(
      "invalid type: null, expected a sequence"
    );

  // Only checkout fresh-validation gets this ONE automatic recovery.
  if (
    !preserveSelection ||
    !isBrokenSupplierSession ||
    !searchMeta.locationId
  ) {
    throw err;
  }

  console.warn(
    "[CHECKOUT DETAILS RECOVERY] Bad supplier search session; creating one fresh searchKey",
    {
      failedSearchKey: freshSearchKey,
      hotelId: getSupplierHotelId(matchedHotel),
    }
  );

  // ONE new Search Init + polling.
  const recoverySearch = await hotelApi.search({
    locationId: searchMeta.locationId,
    checkIn: bookingCheckIn,
    checkOut: bookingCheckOut,
    occupancies: liveOccupancies,
    countryOfResidence:
      searchMeta.countryOfResidence || "India",
    travelType: searchMeta.travelType ?? 1,
    requestId: bookingRequestIdRef.current,
  });

  const recoverySearchKey =
    recoverySearch?.searchKey ||
    recoverySearch?.search_key ||
    "";

  const recoveryHotels =
  recoverySearch?.hotels || [];

const recoveryHotel =
  findSameHotel(recoveryHotels);

  if (!recoverySearchKey || !recoveryHotel) {
    throw new Error(
      "This hotel could not be confirmed in the refreshed supplier session."
    );
  }

  // IMPORTANT: replace BOTH values as a pair.
  freshSearchKey = recoverySearchKey;
  matchedHotel = recoveryHotel;

  console.log(
    "[CHECKOUT DETAILS RECOVERY] New supplier session ready",
    {
      searchKey: freshSearchKey,
      hotelId: getSupplierHotelId(matchedHotel),
    }
  );

  // One Details call using the NEW searchKey + hotelId pair.
  // No further recovery loop.
  data = await hotelApi.details(buildDetailsRequest());
}

      if (mySeq !== roomFetchSeqRef.current) return null;

      const rooms = data?.hotel?.rooms || [];

if (!preserveSelection) {
  setFetchedRooms(rooms);
}

const detailsHotelId =
  data?.hotelId || data?.hotel_id ||
  data?.hotel?.hotelId || data?.hotel?.hotel_id || data?.hotel?.id || "";

if (!freshSearchKey || !detailsHotelId) {
  throw new Error("Supplier searchKey or hotelId is missing. Please restart the hotel search.");
}

if (!preserveSelection) {
  setLiveHotelId(detailsHotelId);
}

      const holdFlag = (value) => {
        if (value === true || value === 1 || ["true", "1", "yes", "available", "allowed"].includes(String(value).toLowerCase())) return true;
        if (value === false || value === 0 || ["false", "0", "no", "unavailable", "not_allowed"].includes(String(value).toLowerCase())) return false;
        return undefined;
      };
      const rateStates = (data?.hotel?.rooms || []).flatMap(r => r.rates || [])
        .map(rate => holdFlag(rate.onHold ?? rate.holdAllowed ?? rate.isHoldAllowed ?? rate.holdAvailable))
        .filter(value => value !== undefined);
      const explicitHold = holdFlag(data?.onHold ?? data?.holdAllowed ?? data?.hotel?.holdBooking ?? data?.hotel?.onHold ?? data?.hotel?.holdAllowed);
      // Do not turn a missing/unknown supplier flag into a false negative.
      const holdSupported = explicitHold ??
        (rateStates.includes(true) ? true : rateStates.length > 0 && rateStates.every(value => value === false) ? false : null);
      if (!preserveSelection) {
  setHoldAllowed(holdSupported);
}
      console.log("[HOLD RAW DUMP] full data:", JSON.stringify(data, null, 2));
      console.log("[HOLD RAW DUMP] hotel keys:", Object.keys(data?.hotel || {}));
      console.log("[HOLD RAW DUMP] first room:", JSON.stringify(data?.hotel?.rooms?.[0], null, 2));
      console.log("[HOLD RAW DUMP] first rate keys:", Object.keys(data?.hotel?.rooms?.[0]?.rates?.[0] || {}));
      // Unknown means the details response did not publish a hold flag. Keep
      // the option available and let the supplier's booking call decide; only
      // an explicit false is a genuine "hold unavailable" result.
      if (holdSupported === false) {
        setBookOnHold(false);
        setHoldTermsAccepted(false);
      }

      if (!preserveSelection) {
  setRoomsLoaded(rooms.length > 0);
  setSelectedRateIndexByRoom({});
  setLiveSearchKey(freshSearchKey);
}
      // Default to 1 room selected — pick the first available (not sold out) room
      const hotelSuggestsBreakfast =
        hotel.amenities?.some(a => a.includes("breakfast")) ||
        hotel.tags?.some(t => t.toLowerCase().includes("breakfast"));

      if (!preserveSelection) {
        const requestedRooms =
          Number(searchMeta.rooms) ||
          Number(searchMeta.occupancies?.length) ||
          1;

        const clickedRoom = hotel.selectedRoom
          ? rooms.find(
            (room) =>
              String(getSupplierRoomId(room)) ===
              String(getSupplierRoomId(hotel.selectedRoom))
          )
          : null;

        if (hotel.selectedRoom && !clickedRoom) {
          setRoomsError(
            `${hotel.selectedRoom.name || "Your selected room"} is no longer available at the current live rate. Please choose another room.`
          );

          setRoomQuantities({});
          setSelectedRateIndexByRoom({});

          return null;
        }

        const roomToSelect = hotel.selectedRoom
          ? clickedRoom
          : (() => {
            const defaultPick = pickDefaultRoomAndRate(
              rooms,
              hotelSuggestsBreakfast
            );

            return defaultPick
              ? rooms.find(
                (room) =>
                  getSupplierRoomId(room) ===
                  defaultPick.roomKey
              )
              : null;
          })();

        if (roomToSelect) {
          const roomKey =
            roomToSelect.id || roomToSelect.name;

          let rateIndex = 0;

          if (clickedRoom && hotel.rateKey) {
            const exactRateIndex =
              (roomToSelect.rates || []).findIndex(
                (rate) =>
                  String(
                    rate.id ||
                    rate.rateKey ||
                    rate.rate_key ||
                    ""
                  ) === String(hotel.rateKey)
              );

            if (exactRateIndex >= 0) {
              rateIndex = exactRateIndex;
            } else {
              const sameRateIndex =
                (roomToSelect.rates || []).findIndex(
                  (rate) => {
                    const sameName = !hotel.rateName || String(rate.name || "")
                      .trim()
                      .toLowerCase() ===
                      String(hotel.rateName || "")
                        .trim()
                        .toLowerCase();
                    const sameType = !hotel.rateType || String(rate.type || "")
                      .trim()
                      .toLowerCase() ===
                      String(hotel.rateType || "")
                        .trim()
                        .toLowerCase();

                    return sameName && sameType;
                  }
                );

              if (sameRateIndex >= 0) {
                rateIndex = sameRateIndex;
              } else {
                setRoomsError(
                  `${hotel.selectedRoom?.name || "Your selected room"} rate has changed. Please select a current rate again.`
                );

                setRoomQuantities({});
                setSelectedRateIndexByRoom({});

                return null;
              }
            }
          } else {
            rateIndex =
              pickDefaultRoomAndRate(
                [roomToSelect],
                hotelSuggestsBreakfast
              )?.rateIndex ?? 0;
          }

          const available = readRoomAvailability(roomToSelect.rates?.[rateIndex]);
          const quantityCap = available === null ? requestedRooms : available;

          setRoomQuantities({
            [roomKey]: Math.min(requestedRooms, quantityCap),
          });

          setSelectedRateIndexByRoom({
            [roomKey]: rateIndex,
          });
        } else {
          setRoomQuantities({});
        }
      }

      return {
  success: true,
  rooms,
  searchKey: freshSearchKey,
  hotelId: detailsHotelId,
  supplierHotelId: getSupplierHotelId(matchedHotel),
  holdAllowed: holdSupported,
};
    } catch (err) {
      if (mySeq !== roomFetchSeqRef.current) {
        if (preserveSelection) {
          throw err;
        }

        return null;
      }

      console.error(
        "[BookingModal] Could not fetch room availability:",
        err.message || err
      );

      setRoomsError(
        err.message ||
        "Could not load live room availability. Please try again."
      );

      if (!preserveSelection) {
        setFetchedRooms([]);
        setRoomsLoaded(false);
        setRoomQuantities({});
        return null;
      }

      // Checkout / hold refresh must NEVER continue using stale availability.
      throw err;
    } finally {
      if (mySeq === roomFetchSeqRef.current) setRoomsLoading(false);
      isFetchingRef.current = false;
    
  }}, [
  hotel.id,
  bookingCheckIn,
  bookingCheckOut,
  searchMeta.locationId,
  searchMeta.occupancies,
  searchMeta.countryOfResidence,
  searchMeta.travelType,
  liveSearchKey,
  liveHotelId,
  holdAllowed,
]);

  useEffect(() => {

    console.log("🔥 MODAL HOTEL:", hotel);
    console.log("🔥 CLICKED ROOM:", hotel.selectedRoom);
    console.log("🔥 CLICKED RATE KEY:", hotel.rateKey);
    console.log("🔥 CLICKED RATE NAME:", hotel.rateName);
    console.log("🔥 CLICKED RATE TYPE:", hotel.rateType);
    console.log("🔥 HOTEL ROOMS:", hotel.rooms);
    const existingRooms =
  Array.isArray(hotel.rooms)
    ? hotel.rooms
    : [];

const existingRoomsHaveRates = existingRooms.some(
  (room) =>
    Array.isArray(room.rates) &&
    room.rates.length > 0
);

if (existingRooms.length > 0 && existingRoomsHaveRates) {
  setFetchedRooms(existingRooms);
  setRoomsLoaded(true);
  setRoomsLoading(false);

      const requestedRooms =
        Number(searchMeta.rooms) ||
        Number(searchMeta.occupancies?.length) ||
        1;

      const selectedRoom = hotel.selectedRoom
        ? existingRooms.find(
          (room) =>
            String(getSupplierRoomId(room)) ===
            String(getSupplierRoomId(hotel.selectedRoom))
        )
        : null;

      if (selectedRoom) {
        const roomKey =
          selectedRoom.id ||
          selectedRoom.name;

        const rates =
          selectedRoom.rates || [];

        let rateIndex = -1;

        // 1. Exact supplier rate ID
        if (hotel.rateKey) {
          rateIndex = rates.findIndex(
            (rate) =>
              String(
                rate.id ||
                rate.rateKey ||
                rate.rate_key ||
                ""
              ) === String(hotel.rateKey)
          );
        }

        // 2. Supplier may regenerate the rate ID.
        // Match the actual selected rate instead.
        if (rateIndex < 0 && (hotel.rateName || hotel.rateType)) {
          rateIndex = rates.findIndex(
            (rate) => {
              const sameName = !hotel.rateName || String(rate.name || "")
                .trim()
                .toLowerCase() ===
                String(hotel.rateName || "")
                  .trim()
                  .toLowerCase();
              const sameType = !hotel.rateType || String(rate.type || "")
                .trim()
                .toLowerCase() ===
                String(hotel.rateType || "")
                  .trim()
                  .toLowerCase();

              return sameName && sameType;
            }
          );
        }

        // IMPORTANT:
        // Never silently convert a selected rate into rate[0].
        if (rateIndex < 0) {
          setRoomsError(
            `${hotel.selectedRoom?.name || "Your selected room"} rate could not be matched. Please select the room again.`
          );

          setRoomQuantities({});
          setSelectedRateIndexByRoom({});
          return;
        }

        const available = readRoomAvailability(rates[rateIndex]);
        const quantityCap = available === null ? requestedRooms : available;

        setRoomQuantities({
          [roomKey]:
            Math.min(
              requestedRooms,
              Math.max(quantityCap, 0)
            ),
        });

        setSelectedRateIndexByRoom({
          [roomKey]: rateIndex,
        });

        return;
      }

      // Detail page already has live rooms, but user
      // entered modal without choosing a specific room.
      setRoomQuantities({});
      setSelectedRateIndexByRoom({});
      return;
    }

    // Only fetch once for the same hotel + dates.
// Prevent duplicate effect execution / React dev StrictMode
// from creating unnecessary supplier search sessions.
const fetchKey = [
  hotel.id,
  bookingCheckIn,
  bookingCheckOut,
].join("|");

if (initialRoomFetchKeyRef.current === fetchKey) {
  return;
}

initialRoomFetchKeyRef.current = fetchKey;

fetchRoomAvailability();

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [hotel.id, bookingCheckIn, bookingCheckOut]);
  const nights = calcNights(bookingCheckIn, bookingCheckOut);
  const totalGuests = useMemo(() => {
    const occ = searchMeta.occupancies || [{ adultCount: 2, childCount: 0 }];
    return occ.reduce((sum, o) => sum + Number(o.adultCount || 0) + Number(o.childCount || 0), 0) || 2;
  }, [searchMeta.occupancies]);
  const getRoomRate = (room) => {
    const roomKey =
      room.id ||
      room.name;

    const rates =
      Array.isArray(room.rates)
        ? room.rates
        : [];

    const selectedIndex =
      selectedRateIndexByRoom[roomKey];

    // A rate was explicitly matched/selected for this room.
    if (selectedIndex !== undefined) {
      return rates[selectedIndex] || null;
    }

    // No specific room was chosen before opening the modal.
    if (!hotel.selectedRoom) {
      return rates[0] || null;
    }

    const isChosenRoom =
      String(getSupplierRoomId(room)) ===
      String(getSupplierRoomId(hotel.selectedRoom));

    // An explicitly selected room must NEVER silently use rate[0].
    if (isChosenRoom) {
      return null;
    }

    return rates[0] || null;
  };

  // Every room the user has added a quantity for (qty > 0)
  const selectedRoomLines = fetchedRooms
    .map((room) => {
      const roomKey = room.id || room.name;
      const qty = roomQuantities[roomKey] || 0;
      const rate = getRoomRate(room);
      return { room, roomKey, qty, rate };
    })
    .filter((line) => line.qty > 0 && line.rate);

  const hasMultiRoomSelection = selectedRoomLines.length > 0;
  const hasLiveRooms =
    roomsLoaded &&
    Array.isArray(fetchedRooms) &&
    fetchedRooms.length > 0;
  const selectedRoomQty = Object.values(roomQuantities).reduce(
    (sum, qty) => sum + Math.max(0, Number(qty || 0)),
    0
  );

  const includedMeals = useMemo(() => {
    const set = new Set();
    const rates = hasMultiRoomSelection
      ? selectedRoomLines.map(l => l.rate)
      : (hotel.selectedRoom
        ? [{ name: hotel.rateName, type: hotel.rateType, roomFacilities: hotel.roomFacilities }]
        : []);
    rates.forEach(rate => getIncludedMealsFromRate(rate).forEach(m => set.add(m)));
    return set;
  }, [hasMultiRoomSelection, selectedRoomLines, hotel.selectedRoom, hotel.rateName, hotel.rateType, hotel.roomFacilities]);
   // This is a supplier-price estimate only.
  // Do not use rate.customerPrice here: it may include admin rules
  // calculated for one room and can repeat per-booking fees when
  // multiplied by room quantity.
  const fallbackTotal = hotel.selectedRoom
    ? (
        Number(hotel.totalPrice) > 0
          ? Number(hotel.totalPrice) * bookingRooms
          : Number(hotel.price || 0) * nights * bookingRooms
      )
    : 0;

  
const roomTotal = hasLiveRooms
  ? (() => {
      const customerTotal = selectedRoomLines.reduce((sum, line) => {
        const rate = line.rate;

        const customerPrice = Number(
          rate?.customerPrice ??
          rate?.adminPricing?.payableTotal ??
          rate?.totalRate ??
          0
        );

        const quantity = Math.max(0, Number(line.qty || 0));

        return (
          sum +
          (Number.isFinite(customerPrice) && customerPrice > 0
            ? customerPrice
            : 0) *
            quantity
        );
      }, 0);

      // These charges are already included in each room's customerPrice,
      // but should apply only once to the booking.
      const duplicateSharedCharges =
        selectedRoomQty > 1
          ? 100 + 80 * Math.max(1, Number(nights) || 1)
          : 0;

      return Math.max(0, customerTotal - duplicateSharedCharges);
    })()
  : fallbackTotal;

  
  const totalSelectedRooms = hasLiveRooms
    ? selectedRoomQty
    : (hotel.selectedRoom ? bookingRooms : 0);

  const requestedRooms =
    Number(searchMeta.rooms) ||
    Number(searchMeta.occupancies?.length) ||
    1;

  const [form, setForm] = useState({
    firstName: user?.name?.split(" ")[0] || "",
    lastName: user?.name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone: user?.phone || "",
    cardNumber: "", expiry: "", cvv: "", cardName: "",
    upiId: "", netbankCode: "", specialReq: "",
  });
  const resolveBookingOccupancies = (roomCount) => {
    const physicalRooms = Math.max(1, Number(roomCount || 1));
    const rawOccupancies = Array.isArray(searchMeta.occupancies)
      ? searchMeta.occupancies
      : [];

    if (rawOccupancies.length === physicalRooms) {
      return rawOccupancies.map(normalizeOccupancyRoom);
    }

    const adultsToAllocate =
      Number(searchMeta.adults) ||
      rawOccupancies.reduce((sum, room) => sum + Number(room.adultCount || 0), 0) ||
      physicalRooms;

    const childrenToAllocate =
      Number(searchMeta.children) ||
      rawOccupancies.reduce((sum, room) => sum + Number(room.childCount || 0), 0) ||
      0;

    const resolvedChildAges = Array.isArray(searchMeta.childAges) && searchMeta.childAges.length > 0
      ? searchMeta.childAges
      : rawOccupancies.flatMap((room) => Array.isArray(room.childAges) ? room.childAges : []);

    return Array.from({ length: physicalRooms }, (_, index) => {
      const baseAdults = Math.floor(adultsToAllocate / physicalRooms);
      const extraAdults = index < adultsToAllocate % physicalRooms ? 1 : 0;

      const baseChildren = Math.floor(childrenToAllocate / physicalRooms);
      const extraChildren = index < childrenToAllocate % physicalRooms ? 1 : 0;

      const childCountForRoom = baseChildren + extraChildren;
      const priorChildren =
        baseChildren * index +
        Math.min(index, childrenToAllocate % physicalRooms);

      return {
        adultCount: baseAdults + extraAdults,
        childCount: childCountForRoom,
        childAges: resolvedChildAges
          .slice(priorChildren, priorChildren + childCountForRoom)
          .map(Number),
      };
    });
  };

  const occupancyRooms = resolveBookingOccupancies(totalSelectedRooms || requestedRooms);

  const totalAdults = occupancyRooms.reduce(
    (sum, room) =>
      sum + Number(room.adultCount || 0),
    0
  );

  const totalChildren = occupancyRooms.reduce(
    (sum, room) =>
      sum + Number(room.childCount || 0),
    0
  );

  const adultCount = totalAdults || Number(searchMeta.adults || 2);
  const childCount = totalChildren || Number(searchMeta.children || 0);

  const [guestDetails, setGuestDetails] = useState(() => [
    ...Array.from({ length: adultCount }, (_, index) => ({
      guestType: "ADULT",
      title: "",
      firstName: index === 0 ? (user?.name?.split(" ")[0] || "") : "",
      lastName: index === 0 ? (user?.name?.split(" ").slice(1).join(" ") || "") : "",
      age: null,
    })),
    ...Array.from({ length: childCount }, (_, index) => ({
      guestType: "CHILD",
      title: "",
      firstName: "",
      lastName: "",
      age: occupancyRooms
        ?.flatMap(o => o.childAges || [])
        ?.[index] ?? "",
    })),
  ]);
  const [errors, setErrors] = useState({});
  const [specialRequests, setSpecialRequests] = useState({});
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstDetails, setGstDetails] = useState({
    gstin: "",
    companyName: "",
    address: "",
  });
  const [mealSelections, setMealSelections] = useState({ breakfast: false, lunch: false, dinner: false });
  const [bookOnHold, setBookOnHold] = useState(false);
  const [holdTermsAccepted, setHoldTermsAccepted] = useState(false);
  const [holdExpiryAt, setHoldExpiryAt] = useState(null);
  const [holdSubmitting, setHoldSubmitting] = useState(false);
  const [holdActionProcessing, setHoldActionProcessing] = useState(false);
  const [holdActionError, setHoldActionError] = useState("");
  const [showReleaseHoldConfirm, setShowReleaseHoldConfirm] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(""); // "Blocked" | "Confirmed" | "Released"
  const [holdCountdown, setHoldCountdown] = useState("");

  // Live countdown for the hold expiry
  useEffect(() => {
    if (!holdExpiryAt) { setHoldCountdown(""); return; }
    const update = () => {
      const diff = new Date(holdExpiryAt).getTime() - Date.now();
      if (!Number.isFinite(diff) || diff <= 0) { setHoldCountdown("Hold Expired"); return; }
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setHoldCountdown(hours > 0 ? `${hours}h ${mins}m ${secs}s` : `${mins}m ${secs}s`);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [holdExpiryAt]);
  // Keep mealSelections in sync with whatever the selected rate already includes
  useEffect(() => {
    if (includedMeals.size === 0) return;
    setMealSelections(prev => {
      const next = { ...prev };
      let changed = false;
      includedMeals.forEach(m => {
        if (!next[m]) { next[m] = true; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [includedMeals]);

  const toggleMeal = (key) => {
    if (includedMeals.has(key)) return; // already bundled in the rate — nothing to toggle
    setMealSelections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const mealLabels = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner" };
  const selectedMealNames = Object.keys(mealSelections).filter(k => mealSelections[k]).map(k => mealLabels[k]); // for display
  const requestedMealNames = Object.keys(mealSelections)
    .filter(k => mealSelections[k] && !includedMeals.has(k))
    .map(k => mealLabels[k]); // for the actual payload — excludes meals already bundled in the rate
  const [showSpecialRequests, setShowSpecialRequests] = useState(false);
  const [savingSpecialRequests, setSavingSpecialRequests] = useState(false);
  const mealAddOns = useMemo(() => {
    const requestedKeys = Object.keys(mealSelections).filter(k => mealSelections[k] && !includedMeals.has(k));
    if (requestedKeys.length === 0) return { total: 0, items: [] };

    const rates = hasMultiRoomSelection
      ? selectedRoomLines.map(l => l.rate)
      : (hotel.selectedRoom
        ? [{ name: hotel.rateName, type: hotel.rateType, additionalCharges: hotel.additionalCharges }]
        : []);

    const items = [];
    let total = 0;

    requestedKeys.forEach((mealKey) => {
      let charge = null;
      for (const rate of rates) {
        charge = getMealChargeFromRate(rate, mealKey);
        if (charge) break;
      }
      if (charge) {
        let amount = charge.amount;
        if (charge.perNight) amount *= nights;
        if (charge.perPerson) amount *= totalGuests;
        total += amount;
        items.push({ meal: mealKey, label: charge.label || mealLabels[mealKey], amount, priced: true });
      } else {
        // Supplier gave no structured charge for this meal — never invent a price.
        items.push({ meal: mealKey, label: mealLabels[mealKey], amount: 0, priced: false });
      }
    });

    return { total, items };
  }, [mealSelections, includedMeals, hasMultiRoomSelection, selectedRoomLines, hotel.selectedRoom, hotel.rateName, hotel.rateType, hotel.additionalCharges, nights, totalGuests]);

  const mealAddOnByKey = useMemo(() => {
    const map = {};
    mealAddOns.items.forEach(item => { map[item.meal] = item; });
    return map;
  }, [mealAddOns]);

  const estimatedGrandTotal = roomTotal + mealAddOns.total;

  const grandTotal = estimatedGrandTotal;

  const isPayDisabled =
  processing ||
  (step === 1 && (!roomsLoaded || roomsLoading || totalSelectedRooms === 0)) ||
  (step === 1 && bookOnHold && !holdTermsAccepted) ||
  (step === 2 && bookOnHold && holdSubmitting) ||
  (step === 2 &&
    !bookOnHold &&
    (
      totalSelectedRooms <= 0 ||
      !Number.isFinite(Number(grandTotal)) ||
      Number(grandTotal) <= 0
    ));

  const paymentTotal =
  step === 3 && Number(checkoutPayload?.totalAmount) > 0
    ? Number(checkoutPayload.totalAmount)
    : grandTotal;
  const saveSpecialRequests = async (selections) => {
    setSavingSpecialRequests(true);
    try {
      await hotelApi.saveSpecialRequests({
        hotelId: hotel.id,
        searchKey: hotel.searchKey || searchMeta.searchKey || "",
        selections,
      });
      setSpecialRequests(selections);
      setShowSpecialRequests(false);
    } catch (err) {
      alert(err.message || "Could not save special requests. Please try again.");
    } finally {
      setSavingSpecialRequests(false);
    }
  };
  const updateForm = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: "" })); };
  const formatCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  const formatExpiry = (v) => { const d = v.replace(/\D/g, "").slice(0, 4); return d.length >= 3 ? d.slice(0, 2) + "/" + d.slice(2) : d; };

  const validateStep = () => {
    const errs = {};

    if (step === 1) {
      const requestedRooms =
        Number(searchMeta.rooms) ||
        Number(searchMeta.occupancies?.length) ||
        1;

      if (totalSelectedRooms !== requestedRooms) {
        errs.rooms =
          `Select ${requestedRooms} room${requestedRooms !== 1 ? "s" : ""} to continue.`;
      }
    }

    if (step === 2) {
      const missingGuest = guestDetails.some(
  (guest) =>
    !String(guest.title || "").trim() ||
    !String(guest.firstName || "").trim() ||
    !String(guest.lastName || "").trim() ||
          (
            guest.guestType === "CHILD" &&
            (
              guest.age === "" ||
              guest.age === null ||
              guest.age === undefined
            )
          )
      );

      if (missingGuest) {
        errs.guests =
          "Please select a title and enter details for all adults and children.";
      }

      if (!missingGuest) {
        const guestNames = guestDetails.map((guest) =>
          `${String(guest.firstName || "").trim().toLowerCase()} ${String(
            guest.lastName || ""
          ).trim().toLowerCase()}`
        );

        if (new Set(guestNames).size !== guestNames.length) {
          errs.guests =
            "Each guest must have a unique first and last name.";
        }
      }

      if (!form.email.includes("@")) {
        errs.email = "Valid email required";
      }

      if (form.phone.replace(/\D/g, "").length < 10) {
        errs.phone = "Valid phone required";
      }

      if (gstEnabled) {
        const gstin = String(gstDetails.gstin || "").trim().toUpperCase();
        if (!GSTIN_REGEX.test(gstin)) {
          errs.gstin = "Enter a valid GSTIN.";
        }
        if (!String(gstDetails.companyName || "").trim()) {
          errs.gstCompanyName = "Company legal name required";
        }
        if (!String(gstDetails.address || "").trim()) {
          errs.gstAddress = "Billing address required";
        }
      }
    }

    if (step === 3) {
      if (payMethod === "card") {
        if (form.cardNumber.replace(/\s/g, "").length !== 16) {
          errs.cardNumber = "Enter a valid 16-digit card number";
        }

        if (!/^\d{2}\/\d{2}$/.test(form.expiry)) {
          errs.expiry = "Enter expiry as MM/YY";
        }

        if (!/^\d{3,4}$/.test(form.cvv)) {
          errs.cvv = "Enter a valid CVV";
        }

        if (!form.cardName.trim()) {
          errs.cardName = "Name on card is required";
        }
      }

      if (payMethod === "upi" && !form.upiId.trim()) {
        errs.upiId = "Enter UPI ID";
      }

      if (payMethod === "netbanking" && !form.netbankCode) {
        errs.netbankCode = "Select a bank";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const buildBookingPayload = (fresh) => {
    const lockedSelection =
  checkoutSelectionRef.current?.selectedRoomLines;

const previousSelection =
  Array.isArray(lockedSelection) && lockedSelection.length > 0
    ? lockedSelection
    : hasLiveRooms
      ? selectedRoomLines
      : hasMultiRoomSelection
        ? selectedRoomLines
        : (
          hotel.selectedRoom
            ? [{
              room: hotel.selectedRoom,
              roomKey:
                hotel.selectedRoom.id ||
                hotel.selectedRoom.name,
              qty: bookingRooms,
              rate: {
                id: hotel.rateKey,
                totalRate: hotel.totalPrice,
                name: hotel.rateName,
                type: hotel.rateType,
              },
            }]
            : []
        );

    const freshRooms =
      Array.isArray(fresh?.rooms) && fresh.rooms.length > 0
        ? fresh.rooms
        : null;

    const roomsToBook = freshRooms
      ? previousSelection.map((line) => {
        const selectedRoomId = getSupplierRoomId(line.room);
        const selectedRoomName = String(line.room?.name || "")
          .trim()
          .toLowerCase();

          console.log("[ROOM MATCH DEBUG]", {
  selectedRoomId,
  selectedRoomName,
  selectedRoomRaw: line.room,
  freshRooms: freshRooms.map((room) => ({
    id: getSupplierRoomId(room),
    name: room?.name,
    normalizedName: String(room?.name || "").trim().toLowerCase(),
    rateRoomIds: (room?.rates || []).map((rate) => rate?.roomId),
  })),
});
        const freshRoom = freshRooms.find(
          (room) =>
            selectedRoomId &&
            String(getSupplierRoomId(room)) ===
            String(selectedRoomId)
        ) || freshRooms.find(
          (room) =>
            selectedRoomName &&
            String(room?.name || "").trim().toLowerCase() ===
            selectedRoomName
        );

        if (!freshRoom) {
          throw new Error(
            `${line.room?.name || "Selected room"} is no longer available. Please select the room again.`
          );
        }

        const freshRates =
          Array.isArray(freshRoom.rates)
            ? freshRoom.rates
            : [];

        const oldRate = line.rate || {};
const selectedRateId = getSupplierRateId(oldRate);

// 1. Exact fresh rateId if supplier kept it
let freshRate = freshRates.find(
  (rate) =>
    selectedRateId &&
    String(getSupplierRateId(rate)) === String(selectedRateId)
);

// 2. Supplier may regenerate rateId.
// Match the SAME semantic rate first.
if (!freshRate) {
  const oldName = String(oldRate.name || "").trim().toLowerCase();
  const oldType = String(oldRate.type || "").trim().toLowerCase();

  const semanticMatches = freshRates.filter((rate) => {
    const freshName = String(rate.name || "").trim().toLowerCase();
    const freshType = String(rate.type || "").trim().toLowerCase();

    const nameMatches = !oldName || freshName === oldName;
    const typeMatches = !oldType || freshType === oldType;

    return nameMatches && typeMatches;
  });

  // Never guess between multiple rates
  if (semanticMatches.length === 1) {
    freshRate = semanticMatches[0];
  }
}

if (!freshRate) {
  throw new Error(
    `The selected rate for ${freshRoom.name || "this room"} is no longer available. Please select the room again.`
  );
}

// 3. After semantic matching, independently verify price.
// Never silently accept a changed price.
const oldPrice = Number(oldRate.totalRate || 0);
const freshPrice = Number(freshRate.totalRate || 0);

if (
  oldPrice > 0 &&
  freshPrice > 0 &&
  Math.abs(freshPrice - oldPrice) >= 0.01
) {
  throw new Error(
    `The price for ${freshRoom.name || "this room"} has changed. Please review the current rate and try again.`
  );
}

if (freshPrice <= 0) {
  throw new Error(
    `The current price for ${freshRoom.name || "this room"} is unavailable.`
  );
}

console.log("[CHECKOUT RATE MATCH]", {
  room: freshRoom.name,
  oldRateId: selectedRateId,
  freshRateId: getSupplierRateId(freshRate),
  rateName: freshRate.name,
  rateType: freshRate.type,
  oldPrice,
  freshPrice,
});

        const available = readRoomAvailability(freshRate);

        if (available !== null && available < Number(line.qty || 0)) {
          throw new Error(
            `Only ${available} room${available !== 1 ? "s are" : " is"} now available for ${freshRoom.name || "this room"}. Please update your selection.`
          );
        }

        return {
          room: freshRoom,
          roomKey:
            freshRoom.id ||
            freshRoom.name,
          qty: Number(line.qty || 0),
          rate: freshRate,
        };
      })
      : previousSelection;

    const selectedPhysicalRoomCount = roomsToBook.reduce(
      (sum, line) => sum + Number(line.qty || 0),
      0
    );
    const bookingOccupancyRooms = resolveBookingOccupancies(
      selectedPhysicalRoomCount || requestedRooms
    );
    const bookingTotalAdults = bookingOccupancyRooms.reduce(
      (sum, room) => sum + Number(room.adultCount || 0),
      0
    );
    const bookingTotalChildren = bookingOccupancyRooms.reduce(
      (sum, room) => sum + Number(room.childCount || 0),
      0
    );

    // Supplier subtotal is used for validation and as a temporary payload
// amount only. It is not the customer's final payable amount.
const refreshedRoomTotal = roomsToBook.reduce((sum, line) => {
  const rate = line.rate || {};

  const supplierRateTotal = Number(
    rate.totalRate ??
    rate.totalAmount ??
    rate.totalPrice ??
    rate.finalPrice ??
    rate.amount ??
    0
  );

  const quantity = Math.max(0, Number(line.qty || 0));

  return (
    sum +
    (Number.isFinite(supplierRateTotal) && supplierRateTotal > 0
      ? supplierRateTotal
      : 0) *
      quantity
  );
}, 0);

if (freshRooms && refreshedRoomTotal <= 0) {
  throw new Error(
    "The refreshed supplier room price is unavailable. Please select the room again."
  );
}

const supplierRoomSubtotal =
  refreshedRoomTotal > 0
    ? refreshedRoomTotal
    : roomTotal;

const preQuoteEstimate =
  supplierRoomSubtotal + Number(mealAddOns.total || 0);

    // Build one roomDetails entry per physical room (expanding quantity > 1 into separate entries)
    const roomDetails = [];
    let occupancyIndex = 0;

    roomsToBook.forEach((line) => {
      for (let i = 0; i < line.qty; i++) {
        const occupancy =
          bookingOccupancyRooms?.[occupancyIndex];

        if (!occupancy) {
          throw new Error("Selected room count does not match the requested occupancies. Please review room selection.");
        }

        const adultsBefore = bookingOccupancyRooms
          ?.slice(0, occupancyIndex)
          .reduce((sum, o) => sum + Number(o.adultCount || 0), 0) || 0;
        const adultsForRoom = guestDetails
          .filter((g) => g.guestType === "ADULT")
          .slice(
            adultsBefore,
            adultsBefore + Number(occupancy.adultCount || 0)
          );

        const childrenBefore = bookingOccupancyRooms
          ?.slice(0, occupancyIndex)
          .reduce((sum, o) => sum + Number(o.childCount || 0), 0) || 0;

        const childrenForRoom = guestDetails
          .filter((g) => g.guestType === "CHILD")
          .slice(
            childrenBefore,
            childrenBefore + Number(occupancy.childCount || 0)
          );

        const roomGuestDetails = [
          ...adultsForRoom.map((guest, index) => ({
            title: guest.title,
            firstName: guest.firstName,
            lastName: guest.lastName,
            isPrimary: index === 0,
            guestType: "ADULT",
          })),

          ...childrenForRoom.map((guest) => ({
            title: guest.title,
            firstName: guest.firstName,
            lastName: guest.lastName,
            isPrimary: false,
            guestType: "CHILD",
            age: Number(guest.age),
          })),
        ];

        const supplierRoomId =
  getSupplierBookingRoomId(line.room, line.rate);

const supplierRateId =
  getSupplierRateId(line.rate);

if (!supplierRoomId || !supplierRateId) {
  throw new Error(
    `Missing supplier room/rate ID for ${line.room?.name || "selected room"}. Please select the room again.`
  );
}

roomDetails.push({
  roomId: supplierRoomId,
  rateId: supplierRateId,
  guestDetails: roomGuestDetails,
});

        occupancyIndex += 1;
      }
    });

    const adultGuestsInRooms = roomDetails.reduce(
      (sum, room) =>
        sum + room.guestDetails.filter((guest) => guest.guestType === "ADULT").length,
      0
    );
    const childGuestsInRooms = roomDetails.reduce(
      (sum, room) =>
        sum + room.guestDetails.filter((guest) => guest.guestType === "CHILD").length,
      0
    );
    const hasInvalidRoomGuests = roomDetails.some((room) => {
      const primaryAdults = room.guestDetails.filter(
        (guest) => guest.guestType === "ADULT" && guest.isPrimary
      ).length;

      return room.guestDetails.length === 0 || primaryAdults !== 1;
    });

    if (
      roomDetails.length !== selectedPhysicalRoomCount ||
      hasInvalidRoomGuests ||
      adultGuestsInRooms !== bookingTotalAdults ||
      childGuestsInRooms !== bookingTotalChildren
    ) {
      throw new Error("Guest allocation is incomplete for the selected rooms. Please review guest details and try again.");
    }

    const specialRequestItems = buildSpecialRequestsItems({
      specialRequests,
      requestedMealNames,
      freeText: form.specialReq,
      mealAddOnItems: mealAddOns.items,
    });
    const rateKey =
      roomsToBook[0]?.rate?.id ||
      roomsToBook[0]?.rate?.rateKey ||
      roomsToBook[0]?.rate?.rate_key ||
      hotel.rateKey ||
      "";
    const roomSelections = roomsToBook.map((line) => ({
      roomId:
        getSupplierBookingRoomId(line.room, line.rate),
      roomName:
        line.room?.name ||
        "Room",
      rateId:
        getSupplierRateId(line.rate),
      rateName:
        line.rate?.name ||
        line.rate?.type ||
        "",
      qty: Number(line.qty || 0),
      totalRate: Number(
        line.rate?.totalRate || 0
      ),
      refundable:
        line.rate?.refundable ?? null,
    }));
    const guests = guestDetails.map((guest, index) => ({
      title: guest.title,
      firstName: guest.firstName,
      lastName: guest.lastName,
      guestType: guest.guestType,
      isPrimary: index === 0,
      ...(guest.guestType === "CHILD"
        ? { age: Number(guest.age) }
        : {}),
    }));
    const finalRequestId = searchMeta.requestId || bookingRequestIdRef.current;
    const finalSearchKey = fresh?.searchKey || liveSearchKey || hotel.searchKey || searchMeta.searchKey || "";
    const finalHotelId = fresh?.hotelId || liveHotelId;

    if (!finalRequestId || !finalSearchKey || !finalHotelId) {
      throw new Error("Supplier requestId, searchKey, or hotelId is missing. Please restart the hotel search.");
    }

    return {
  requestId: finalRequestId,
  searchKey: finalSearchKey,
  onHold: bookOnHold,
  hotelId: finalHotelId,
  supplierHotelId:
    fresh?.supplierHotelId || getSupplierHotelId(hotel),
  rateKey,
      guests,
      countryCode: "+91",
      guestContactNumber: form.phone,
      guestEmail: form.email,
      roomAllocation: roomDetails,
      roomDetails,
      roomSelections,
      totalAmount: preQuoteEstimate,
supplierTotalAmount: supplierRoomSubtotal,
      paymentMethod: bookOnHold ? "hold" : "easebuzz",
      hotelName: hotel.name,
      nights,
      checkIn: bookingCheckIn,
      checkOut: bookingCheckOut,
      occupancies: bookingOccupancyRooms,
      destinationCountry: searchMeta.destinationCountry || "IN",
      travelType: Number(searchMeta.travelType ?? 0),
      countryOfResidence: searchMeta.countryOfResidence || "India",

      // Fields your confirm page (HotelConfirm) reads for display purposes:
      id: hotel.id,
      name: hotel.name,
      location: hotel.location,
      stars: hotel.stars,
      image: hotel.image,
      rooms: selectedPhysicalRoomCount,
      adults: bookingTotalAdults,
      children: bookingTotalChildren,
      price:
        preQuoteEstimate> 0 &&
          nights > 0 &&
          selectedPhysicalRoomCount > 0
          ? Number(
            (
              preQuoteEstimate /
              nights /
              selectedPhysicalRoomCount
            ).toFixed(2)
          )
          : hotel.price,
      totalPrice: preQuoteEstimate,
      grandTotal: preQuoteEstimate,
      guestFirstName: guestDetails[0]?.firstName || "",
      guestLastName: guestDetails[0]?.lastName || "",
      guestPhone: form.phone,
      specialRequests: specialRequestItems,                                  // structured array for the API
      specialRequestsText: specialRequestItems.map(i => i.label).join(", "), // human-readable fallback for display screens
      ...(gstEnabled ? {
        gstDetails: {
          enabled: true,
          gstin: String(gstDetails.gstin || "").trim().toUpperCase(),
          companyName: String(gstDetails.companyName || "").trim(),
          address: String(gstDetails.address || "").trim(),
        },
      } : {}),
      mealPreferences: selectedMealNames,   // full set incl. included meals, for display
      includedMeals: Array.from(includedMeals),
      isOnHold: bookOnHold,
    };
  };

  const createBooking = async (payload) => {
  try {
    console.log("[createBooking] Payload before hotelApi.book:", {
      paymentStatus: payload?.paymentStatus,
      paymentMethod: payload?.paymentMethod,
      txnid: payload?.txnid,
      easepayid: payload?.easepayid,
      bookingReference: payload?.bookingReference,
    });

    const data = await hotelApi.book(payload);
      setConfirmedRef(data.bookingReference || data.supplier?.bookingRefNo || "");
      setConfirmedBookingId(data.id || null);
      setHoldExpiryAt(data.holdExpiryAt || data.supplier?.holdExpiryAt || null);
      setBookingStatus(data.bookingStatus || data.supplier?.bookingStatus || "");
      setStep(4);
    } catch (err) {
      console.error("[createBooking] failed:", err.message, err.details, err);
      throw err;
    }
  };
  

  // Authoritative server quote for the current selection.
  // Validate it before attaching pricing to the booking payload.
  const createQuoteForPayload = async (payload) => {
    console.log("QUOTE HOTEL IDs:", {
  hotelId: payload.hotelId,
  supplierHotelId: payload.supplierHotelId,
});
    const quote = await createHotelQuote({
      searchKey: payload.searchKey,
      hotelId: payload.hotelId,
      checkIn: payload.checkIn,
      checkOut: payload.checkOut,
      requestId: payload.requestId,
      supplierHotelId: payload.supplierHotelId,
      roomSelections: (payload.roomSelections || []).map((s) => ({
        roomId: s.roomId,
        rateId: s.rateId,
        qty: s.qty,
      })),
      mealKeys: (payload.specialRequests || [])
        .filter((item) => item && item.category === "meal" && item.code)
        .map((item) => String(item.code).toLowerCase()),
      occupancies: searchMeta.occupancies || [],
    });

    const payableTotal = Number(quote?.payableTotal);

    if (
      !quote?.quoteId ||
      !Number.isFinite(payableTotal) ||
      payableTotal <= 0
    ) {
      throw new Error(
        "Could not verify the final hotel price. Please try again."
      );
    }

    payload.quoteId = quote.quoteId;
    payload.pricing = quote;

    return quote;
  };


  const handleHoldBooking = async () => {
    bookingRequestIdRef.current = searchMeta.requestId || bookingRequestIdRef.current;
    setHoldSubmitting(true);
    setHoldActionError("");
    try {
      const fresh = await fetchRoomAvailability({
        preserveSelection: true,
      });
      console.log("[HOLD DEBUG] After refresh — holdAllowed:", fresh?.holdAllowed, "fresh rooms:", fresh?.rooms?.map(r => ({ name: r.name, onHold: r.rates?.[0]?.onHold })));
      if (!fresh || !fresh.searchKey || !fresh.hotelId) {
        setHoldActionError("Could not refresh availability. Please try again.");
        return;
      }
      // Per supplier: only attempt onHold:true if Details API says this hotel/rate supports it
      if (fresh.holdAllowed === false) {
        setHoldActionError("Blocked booking is not available for this rate anymore. Please pay now to confirm.");
        setBookOnHold(false);
        return;
      }
      const payload = {
        ...buildBookingPayload(fresh),
        onHold: true,
        paymentMethod: "hold",
        isOnHold: true,
      };
      const stillHoldable = payload.roomSelections.every((selection) => {
        const room = fresh.rooms.find((r) => String(getSupplierRoomId(r)) === String(selection.roomId));
        const rate = (room?.rates || []).find((r) => String(getSupplierRateId(r)) === String(selection.rateId));
        return rate?.onHold !== false;
      });
      if (!stillHoldable) {
        setHoldActionError("Hold is no longer available for the selected room(s). Please reselect.");
        setBookOnHold(false);
        return;
      }
      
      const holdQuote = await createQuoteForPayload(payload);
      const holdPayableTotal = Number(holdQuote.payableTotal);

      if (
        !holdQuote.quoteId ||
        !Number.isFinite(holdPayableTotal) ||
        holdPayableTotal <= 0
      ) {
        throw new Error(
          "Could not verify the final hotel price. Please try again."
        );
      }

      payload.supplierTotalAmount = Number(
  holdQuote.supplier?.roomTotal ??
  payload.supplierTotalAmount ??
  0
);
      payload.totalAmount = holdPayableTotal;
      payload.grandTotal = holdPayableTotal;
      payload.totalPrice = holdPayableTotal;
      payload.quoteId = holdQuote.quoteId;
      payload.pricing = holdQuote;

      setCheckoutPayload({
        totalAmount: holdPayableTotal,
        quoteId: holdQuote.quoteId,
      });

      const data = await hotelApi.book(payload);

      setConfirmedRef(data.bookingReference || data.supplier?.bookingRefNo || "");
      setHoldExpiryAt(data.holdExpiryAt || data.supplier?.holdExpiryAt || null);
      setBookingStatus(data.bookingStatus || data.supplier?.bookingStatus || "Blocked");
      setStep(4);
    } catch (err) {
      console.error("[HoldBooking] failed:", err.message, err.details, err);
      setHoldActionError(err.details || err.message || "Could not hold this room. Please try again.");
    } finally {
      setHoldSubmitting(false);
    }
  };

  const handleConfirmHold = async () => {
    if (holdActionProcessing || showPaymentCheckout) return;
    setHoldActionError("");
    setPaymentError("");
    if (!confirmedRef) {
      setHoldActionError("Booking reference is missing for this hold.");
      return;
    }
    setPaymentPurpose("hold-confirm");
    setShowPaymentCheckout(true);
    setStep(3);
  };

  const handleReleaseHold = async () => {
    if (holdActionProcessing) return;
    setShowReleaseHoldConfirm(true);
  };

  const confirmReleaseHold = async () => {
    if (holdActionProcessing) return;
    setHoldActionProcessing(true);
    setHoldActionError("");
    try {
      const data = await hotelApi.holdConfirm(confirmedRef, "RELEASE", bookingRequestIdRef.current);
      setBookingStatus(data.bookingStatus || data.supplier?.bookingStatus || "Released");
      setShowReleaseHoldConfirm(false);
      onClose();
    } catch (err) {
      console.error("[ReleaseHold] failed:", err.message, err.details, err);
      setHoldActionError(err.details || err.message || "Could not release this hold.");
    } finally {
      setHoldActionProcessing(false);
    }
  };

  const nextStep = () => {
    if (!validateStep()) return;

    if (step === 2) {
  if (bookOnHold) {
    handleHoldBooking();
    return;
  }

  checkoutSelectionRef.current = {
    roomQuantities: { ...roomQuantities },
    selectedRateIndexByRoom: { ...selectedRateIndexByRoom },
    selectedRoomLines: selectedRoomLines.map((line) => ({
      ...line,
    })),
    roomTotal,
    grandTotal,
    totalSelectedRooms,
  };

  (async () => {
        setProcessing(true);
        setPaymentError("");

        try {
          bookingRequestIdRef.current = searchMeta.requestId || bookingRequestIdRef.current;

          const fresh = await fetchRoomAvailability({
            preserveSelection: true,
          });

          if (
            !fresh ||
            fresh.success !== true ||
            !fresh.searchKey ||
            !fresh.hotelId ||
            !Array.isArray(fresh.rooms) ||
            fresh.rooms.length === 0
          ) {
            throw new Error(
              "Could not confirm live room availability. Please try again before payment."
            );
          }
          console.log("[CHECKOUT FRESH VALIDATED]", {
            requestId: bookingRequestIdRef.current,
            searchKey: fresh.searchKey,
            hotelId: fresh.hotelId,
            roomCount: fresh.rooms.length,
          });
          const payload = buildBookingPayload(fresh);
          console.log("[CHECKOUT PAYLOAD VALIDATED]", {
            requestId: payload.requestId,
            searchKey: payload.searchKey,
            hotelId: payload.hotelId,
            roomDetails: payload.roomDetails?.map((room) => ({
              roomId: room.roomId,
              rateId: room.rateId,
              guests: room.guestDetails?.length || 0,
            })),
          });

          
          // Get the authoritative customer price from the backend.
          const quote = await createQuoteForPayload(payload);

          const payableTotal = Number(quote.payableTotal);

          if (
            !quote.quoteId ||
            !Number.isFinite(payableTotal) ||
            payableTotal <= 0
          ) {
            throw new Error(
              "Could not verify the final hotel price. Please try again."
            );
          }

          
// Keep the supplier subtotal separate from the customer payable amount.
payload.supplierTotalAmount = Number(
  quote.supplier?.roomTotal ??
  payload.supplierTotalAmount ??
  0
);


          // Use the server-calculated payable amount for checkout.
          payload.totalAmount = payableTotal;
          payload.grandTotal = payableTotal;
          payload.totalPrice = payableTotal;
          payload.price =
            nights > 0 && Number(payload.rooms) > 0
              ? Number(
                  (payableTotal / nights / Number(payload.rooms)).toFixed(2)
                )
              : payableTotal;

          // Preserve quoteId and the full pricing breakdown.
          payload.quoteId = quote.quoteId;
          payload.pricing = quote;

          saveHotelBooking(payload);
          setProcessing(false);
          navigate("/hotel-confirm", {
            state: { booking: payload },
          });

        } catch (err) {
  const locked = checkoutSelectionRef.current;

  if (locked) {
    setRoomQuantities(locked.roomQuantities);
    setSelectedRateIndexByRoom(
      locked.selectedRateIndexByRoom
    );
  }

  console.error(
    "[Checkout] Could not refresh booking availability:",
    err
  );

  setPaymentError(
    "Due to a temporary issue, we couldn't process your booking right now. Please try again."
  );

  setProcessing(false);
}
      })();

      return;
    }

    setStep((s) => s + 1);
  };
  const handlePaymentSuccess = async (response) => {
    const isHoldConfirmPayment = paymentPurpose === "hold-confirm";
    if (isHoldConfirmPayment) {
      if (holdConfirmInFlightRef.current) return;
      holdConfirmInFlightRef.current = true;
    }
    setShowPaymentCheckout(false);
    setProcessing(true);
    setHoldActionProcessing(isHoldConfirmPayment);
    try {
      if (isHoldConfirmPayment) {
        // Never trust the browser redirect: verify signature / gateway status
        // server-side BEFORE confirming the hold.
        const verification = await verifyHotelEasebuzzPayment(response, token);
        if (!verification.verified) {
          throw new Error(
            "Payment could not be verified yet. Please wait a moment and try again."
          );
        }
        const data = await hotelApi.holdConfirm(confirmedRef, "CONFIRM", bookingRequestIdRef.current, {
          paymentMethod: "Easebuzz",
          paymentTxnId: response?.txnid || null,
          easepayId: response?.easepayid || null,
        });
        setBookingStatus(data.bookingStatus || data.supplier?.bookingStatus || "Confirmed");
        setHoldExpiryAt(data.holdExpiryAt ?? data.supplier?.holdExpiryAt ?? null);
        setBookOnHold(false);
        setPaymentPurpose("booking");
        setHoldActionProcessing(false);
        setProcessing(false);
        setStep(1);
        onClose();
        navigate("/profile?tab=hotels&filter=upcoming");
        return;
      }
      if (!checkoutPayload) {
        throw new Error("Validated hotel booking data is missing. Please restart checkout.");
      }

      await createBooking({
  ...checkoutPayload,
  txnid: response?.txnid || checkoutPayload.txnid || null,
  easepayid: response?.easepayid || checkoutPayload.easepayid || null,
  paymentStatus: "success",
});
    } catch (err) {
      console.error("[HotelPaymentSuccess] failed:", err.message, err.details, err);
      const message = err.details || err.message || "Payment succeeded, but we could not complete the hotel action. Please contact support.";
      if (isHoldConfirmPayment) {
        setHoldActionError(message);
        setStep(4);
      } else {
        setPaymentError(message);
      }
    } finally {
      if (isHoldConfirmPayment) holdConfirmInFlightRef.current = false;
      setHoldActionProcessing(false);
      setProcessing(false);
    }
  };

  const handlePaymentFailure = (response) => {
    setPaymentError(response?.error_Message || "Payment was not completed. Please try again.");
  };

  const adjustNights = (delta) => { const n = Math.max(1, nights + delta); setBookingCheckOut(addDays(bookingCheckIn, n)); };
  const handleCheckInChange = (val) => { setBookingCheckIn(val); setBookingCheckOut(addDays(val, nights)); };

  const banks = [
    { code: "sbi", label: "State Bank of India" },
    { code: "hdfc", label: "HDFC Bank" },
    { code: "icici", label: "ICICI Bank" },
    { code: "axis", label: "Axis Bank" },
    { code: "kotak", label: "Kotak Mahindra" },
  ];
  const amenityIcons = {
    pool: <Waves size={18} />, wifi: <Wifi size={18} />, spa: <HeartHandshake size={18} />,
    gym: <Dumbbell size={18} />, restaurant: <UtensilsCrossed size={18} />,
    bar: <Martini size={18} />, parking: <ParkingCircle size={18} />, ac: <Snowflake size={18} />,
  };
  const amenityLabels = { pool: "Pool", wifi: "Wi-Fi", spa: "Spa", gym: "Gym", restaurant: "Restaurant", bar: "Bar", parking: "Parking", ac: "A/C" };
  const isConfirmingHold = paymentPurpose === "hold-confirm" && holdActionProcessing;
  const isHoldActionLocked = holdActionProcessing;

  return (
    <div className="ht-bm-overlay" onClick={(e) => e.target === e.currentTarget && !isHoldActionLocked && onClose()}>
      <div className="ht-bm-modal">
        <div className="ht-bm-header">
          <div className="ht-bm-header-left">
            <div className="ht-bm-title">
              {step === 4
                ? (bookOnHold ? "🕐 Room Held" : "✅ Booking Confirmed!")
                : "Complete Your Booking"}
            </div>

            {step < 4 && (
              <div className="ht-bm-steps">
                {["Stay Details", "Guest Info", "Payment"].map((s, i) => (
                  <div key={i} className={`ht-bm-step${step > i + 1 ? " done" : step === i + 1 ? " active" : ""}`}>
                    <div className="ht-bm-step-num">{step > i + 1 ? "✓" : i + 1}</div>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="ht-bm-close" onClick={onClose} disabled={isHoldActionLocked}>✕</button>
        </div>

        <div className="ht-bm-body">
          {isConfirmingHold && (
            <div style={{ padding: "18px 24px", fontSize: 14, fontWeight: 700, color: "#0f766e" }}>
              Confirming your booking...
            </div>
          )}
          {step === 4 && (
            <div style={{ padding: 24 }}>
              {bookOnHold ? (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: 18 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#92400e" }}>
                    Your room is on hold — not paid yet
                  </div>
                  <div style={{ fontSize: 13, color: "#78350f", marginTop: 6 }}>
                    Booking reference: <strong>{confirmedRef || "—"}</strong><br />
                    Status: <strong>{bookingStatus || "Blocked"}</strong>
                  </div>
                  {holdExpiryAt && (
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#b45309", marginTop: 10 }}>
                      ⏳ Time left: {holdCountdown}
                    </div>
                  )}
                  {!holdExpiryAt && (
                    <div style={{ fontSize: 12.5, color: "#92400e", marginTop: 10 }}>
                      No expiry time was returned by the hotel for this hold — please confirm or release before checking out to avoid losing your room.
                    </div>
                  )}

                  {holdActionError && (
                    <div style={{ fontSize: 12.5, color: "#b91c1c", marginTop: 10 }}>
                      ⚠️ {holdActionError}
                    </div>
                  )}

                  {showReleaseHoldConfirm && (
                    <div style={{ marginTop: 14, padding: 14, border: "1px solid #fed7aa", borderRadius: 10, background: "#fff7ed" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
                        Release this held room?
                      </div>
                      <div style={{ fontSize: 12.5, color: "#78350f", marginTop: 4 }}>
                        This will release the supplier hold for booking reference <strong>{confirmedRef || "—"}</strong>.
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                        <button
                          type="button"
                          className="ht-bm-btn-back"
                          style={{ flex: 1 }}
                          disabled={holdActionProcessing}
                          onClick={() => setShowReleaseHoldConfirm(false)}
                        >
                          Keep Hold
                        </button>
                        <button
                          type="button"
                          className="ht-bm-btn-primary"
                          style={{ flex: 1 }}
                          disabled={holdActionProcessing}
                          onClick={confirmReleaseHold}
                        >
                          {holdActionProcessing ? "Releasing..." : "Release Hold"}
                        </button>
                      </div>
                    </div>
                  )}

                  {!showReleaseHoldConfirm && (
  <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
    <button
      type="button"
      className="ht-bm-btn-primary"
      style={{ flex: 1 }}
      disabled={
        holdActionProcessing ||
        holdCountdown === "Hold Expired"
      }
      onClick={handleConfirmHold}
    >
      {holdCountdown === "Hold Expired"
        ? "Hold Expired"
        : holdActionProcessing
          ? "Processing…"
          : "Confirm & Pay Now"}
    </button>

    <button
      type="button"
      className="ht-bm-btn-back"
      style={{ flex: 1 }}
      disabled={holdActionProcessing}
      onClick={handleReleaseHold}
    >
      Release Hold
    </button>
  </div>
)}
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#0d1b3e" }}>
                    Your booking is confirmed!
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>
                    Reference: <strong>{confirmedRef}</strong>
                  </div>
                  <button type="button" className="ht-bm-btn-primary" style={{ marginTop: 16 }} onClick={onClose}>
                    Done
                  </button>
                </div>
              )}
            </div>
          )}


          {!isConfirmingHold && step < 4 && (
            <div className="ht-bm-two-col">
              <div className="ht-bm-left">
                {step === 1 && (
                  <div className="ht-bm-section">
                    <div className="ht-bm-hotel-preview">
                      <img src={hotel.image} alt={hotel.name} className="ht-bm-hotel-img" />
                      <div className="ht-bm-hotel-info">
                        <div className="ht-bm-hotel-name">{hotel.name}</div>
                        <div className="ht-bm-hotel-stars">{renderStars(hotel.stars)}</div>
                        <div className="ht-bm-hotel-loc">📍 {hotel.location}</div>
                      </div>
                    </div>
                    {roomsLoading && (
                      <div style={{ padding: "12px 0", fontSize: 12.5, color: "#64748b" }}>
                        Checking live availability for your dates…
                      </div>
                    )}
                    {roomsError && (
                      <div style={{ padding: "10px 12px", fontSize: 12, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, marginBottom: 10 }}>
                        ⚠️ {roomsError}<br />
                        <span style={{ fontSize: 11.5, color: "#92400e" }}>
                          Please try again in a moment, or pick a different hotel — booking can't continue without confirmed room availability.
                        </span>
                      </div>
                    )}
                    {!roomsLoading && fetchedRooms?.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: "#0d1b3e", marginBottom: 4, textTransform: "uppercase" }}>
                          Choose Your Rooms
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 10 }}>
                          You can select more than one room type. Quantity is limited by real-time availability.
                        </div>
                        {errors.rooms && (
                          <div
                            style={{
                              marginTop: 8,
                              color: "#b91c1c",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            {errors.rooms}
                          </div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {fetchedRooms.map((room) => {
                            const roomKey = room.id || room.name;
                            const rIndex = selectedRateIndexByRoom[roomKey] ?? 0;
                            const rRates = room.rates?.length > 0 ? room.rates : [];
                            const rActiveRate = rRates[rIndex] || rRates[0] || {};
                            const available = readRoomAvailability(rActiveRate);
                            const qty = roomQuantities[roomKey] || 0;
                            const soldOut = available !== null && available <= 0;

                            const requestedRooms =
                              Number(searchMeta.rooms) ||
                              Number(searchMeta.occupancies?.length) ||
                              1;

                            const setQty = (nextQty) => {
                              const otherSelectedRooms = selectedRoomQty - qty;

                              const maxForThisRoom = Math.max(
                                0,
                                requestedRooms - otherSelectedRooms
                              );

                              const clamped = Math.max(
                                0,
                                Math.min(
                                  available === null ? maxForThisRoom : available,
                                  maxForThisRoom,
                                  nextQty
                                )
                              );

                              setRoomQuantities(prev => ({
                                ...prev,
                                [roomKey]: clamped,
                              }));
                            };

                            return (
                              <div
                                key={roomKey}
                                style={{
                                  border: qty > 0 ? "2px solid #0f766e" : "1px solid #e2e8f0",
                                  borderRadius: 12,
                                  padding: 12,
                                  background: soldOut ? "#f8fafc" : qty > 0 ? "#ecfdf5" : "#fff",
                                  opacity: soldOut ? 0.6 : 1,
                                }}
                              >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 13.5, color: "#0d1b3e" }}>
                                      {room.name || "Room"}
                                    </div>
                                    {room.roomSize && (
                                      <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                                        {room.roomSize}
                                      </div>
                                    )}
                                    <div style={{ fontSize: 11, marginTop: 4, color: soldOut ? "#b91c1c" : "#059669", fontWeight: 600 }}>
                                      {soldOut
                                        ? "Sold out for these dates"
                                        : available === null
                                          ? "Availability on request"
                                          : `${available} room${available !== 1 ? "s" : ""} available`}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "right" }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f766e" }}>
  {fmt(
    Number(
      rActiveRate.customerPrice ??
      rActiveRate.adminPricing?.payableTotal ??
      rActiveRate.totalRate ??
      0
    )
  )}
</div>
                                    <div style={{ fontSize: 12.5, color: "#5e5e5e" }}>per room, total stay</div>
                                  </div>
                                </div>

                                {rRates.length > 1 && (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                                    {rRates.map((rate, idx) => {
                                      const isRateActive = idx === rIndex;
                                      return (
                                        <button
                                          key={rate.id || idx}
                                          type="button"
                                          disabled={soldOut}
                                          onClick={() =>
                                            setSelectedRateIndexByRoom(prev => ({ ...prev, [roomKey]: idx }))
                                          }
                                          style={{
                                            padding: "6px 10px",
                                            borderRadius: 8,
                                            border: isRateActive ? "2px solid #0f766e" : "1px solid #cbd5e1",
                                            background: isRateActive ? "#fff" : "#f8fafc",
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            cursor: soldOut ? "not-allowed" : "pointer",
                                          }}
                                        >
                                          {rate.name || rate.type || `Option ${idx + 1}`} · {fmt(
  Number(
    rate.customerPrice ??
    rate.adminPricing?.payableTotal ??
    rate.totalRate ??
    0
  )
)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, marginTop: 10 }}>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: "#0d1b3e" }}>Rooms</span>
                                  <button
                                    type="button"
                                    disabled={soldOut || qty <= 0}
                                    onClick={() => setQty(qty - 1)}
                                    style={{ width: 26, height: 26, borderRadius: "50%", border: "1px solid #cbd5e1", background: "#fff", cursor: qty <= 0 ? "not-allowed" : "pointer" }}
                                  >−</button>
                                  <span style={{ width: 20, textAlign: "center", fontWeight: 700 }}>{qty}</span>
                                  <button
                                    type="button"
                                    disabled={
                                      soldOut ||
                                      (available !== null && qty >= available) ||
                                      selectedRoomQty >= requestedRooms
                                    }
                                    onClick={() => setQty(qty + 1)}
                                    style={{
                                      width: 26, height: 26, borderRadius: "50%", border: "1px solid #cbd5e1", background: "#fff", cursor:
                                        soldOut ||
                                          (available !== null && qty >= available) ||
                                          selectedRoomQty >= requestedRooms
                                          ? "not-allowed"
                                          : "pointer"
                                    }}
                                  >+</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="ht-bm-date-grid">
                      <div className="ht-bm-date-box">
                        <div className="ht-bm-date-label">Check-In</div>
                        <input type="date" className="ht-bm-date-input" value={bookingCheckIn} onChange={e => handleCheckInChange(e.target.value)} />
                        <div className="ht-bm-date-hint">After 2:00 PM</div>
                      </div>
                      <div className="ht-bm-date-arrow">→</div>
                      <div className="ht-bm-date-box">
                        <div className="ht-bm-date-label">Check-Out</div>
                        <input type="date" className="ht-bm-date-input" value={bookingCheckOut} min={addDays(bookingCheckIn, 1)} onChange={e => setBookingCheckOut(e.target.value)} />
                        <div className="ht-bm-date-hint">Before 11:00 AM</div>
                      </div>
                    </div>
                    <div className="ht-bm-live-price">
  <div className="ht-bm-live-price-row">
    <span>
      Room amount estimate · {nights}N · {totalSelectedRooms}R
    </span>
    <strong>{fmt(roomTotal)}</strong>
  </div>

  <div
    className="ht-bm-price-row"
    style={{ color: "#6b7280", fontSize: "12px" }}
  >
    <span>Fees and applicable GST</span>
    <span>Confirmed at checkout</span>
  </div>

  <div className="ht-bm-live-price-total">
    <span>Estimated total</span>
    <strong>{fmt(grandTotal)}</strong>
  </div>

  <div
    style={{
      fontSize: "11px",
      color: "#6b7280",
      marginTop: 4,
    }}
  >
    Estimate only · final customer payable amount is confirmed by the
    server quote at checkout.
  </div>
</div>
                    <div className="ht-bm-stay-info">
                      <div className="ht-bm-stay-row"><span><Moon size={16} /> Duration</span><strong>{nights} Night{nights !== 1 ? "s" : ""}</strong></div>
                      <div className="ht-bm-stay-row"><span><Bed size={16} /> Rooms</span><strong>{totalSelectedRooms} room{totalSelectedRooms !== 1 ? "s" : ""}</strong></div>
                      <div className="ht-bm-stay-row">
                        <span><Users size={16} /> Guests</span>
                        <strong>
                          {adultCount} Adult{adultCount !== 1 ? "s" : ""}
                          {childCount > 0
                            ? `, ${childCount} Child${childCount !== 1 ? "ren" : ""}`
                            : ""}
                        </strong>
                      </div>
                      <div className="ht-bm-stay-row">
                        <span><Tag size={16} /> Room Type</span>
                        <strong>
                          {selectedRoomLines.length > 0
                            ? selectedRoomLines
                              .map(
                                (line) =>
                                  `${line.room.name || "Room"}${line.qty > 1 ? ` × ${line.qty}` : ""}`
                              )
                              .join(" | ")
                            : roomsLoaded
                              ? "No room selected"
                              : hotel.selectedRoom?.name || "Room"}
                        </strong>
                      </div>
                    </div>
                    {/* {hotel.amenities?.length > 0 && (
                      <div className="ht-bm-amenity-row">
                        {hotel.amenities.slice(0, 5).map(a => (
                          <div key={a} className="ht-bm-amenity-chip">{amenityIcons[a]} {amenityLabels[a]}</div>
                        ))}
                      </div>
                    )} */}
                    {holdAllowed === false && (
                      <div style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: 12, borderRadius: 10, border: "1px solid #fecaca",
                        background: "#fef2f2", marginTop: 14,
                      }}>
                        <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b" }}>
                            Hold-and-pay-later not available
                          </div>
                          <div style={{ fontSize: 11.5, color: "#7f1d1d", marginTop: 2 }}>
                            This hotel/rate requires immediate payment to confirm the booking. You'll pay now via EasyBuzz to secure this room.
                          </div>
                        </div>
                      </div>
                    )}

                    {holdAllowed !== false && (
                      <div style={{
                        borderRadius: 10, border: "1px solid #e2e8f0",
                        background: bookOnHold ? "#fffbeb" : "#fff", marginTop: 14, overflow: "hidden",
                      }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: 12 }}>
          <input
                            type="checkbox"
                            id="hold-booking-cb"
                            checked={bookOnHold}
                            onChange={(e) => {
                              setBookOnHold(e.target.checked);
                              if (!e.target.checked) setHoldTermsAccepted(false);
                            }}
                            style={{ marginTop: 2 }}
                          />
                          <label htmlFor="hold-booking-cb" style={{ cursor: "pointer" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#0d1b3e" }}>
                              Hold this room (pay later)
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                              Reserve now without paying. The hotel will hold your room for a limited time.
                            </div>
                          </label>
                        </div>

                        {bookOnHold && (
                          <div style={{ padding: "0 12px 12px 40px" }}>
                            <div style={{
                              fontSize: 11.5, color: "#78350f", background: "#fef9c3",
                              border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 10,
                            }}>
                              <strong style={{ display: "block", marginBottom: 4 }}>Hold terms:</strong>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                <li>Your room is reserved but <strong>not paid</strong> until you confirm.</li>
                                <li>You must Confirm &amp; Pay before the hold expiry shown after booking, or the room may be released automatically.</li>
                                <li>Room availability and rate are re-checked live — if sold out or changed, you'll be asked to reselect.</li>
                                <li>You can release the hold anytime before confirming, with no charge.</li>
                              </ul>
                            </div>
                            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={holdTermsAccepted}
                                onChange={(e) => setHoldTermsAccepted(e.target.checked)}
                                style={{ marginTop: 2 }}
                              />
                              <span style={{ fontSize: 12, color: "#0d1b3e", fontWeight: 600 }}>
                                I understand and accept these hold terms
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 10 }}>
                      By proceeding, I agree to the{" "}
                      <span
                        style={{ color: "#2563eb", cursor: "pointer", fontWeight: 600 }}
                        onClick={() => {
                          onClose();
                          navigate(`/hotelbooking/${hotel.id}#hotel-policies`);
                        }}
                      >
                        Cancellation &amp; Property Booking Policies
                      </span>.
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="ht-bm-section">
                    <div className="ht-bm-sec-title">Guest Information</div>
                    {guestDetails.map((guest, index) => (
                      <div key={index} style={{ marginBottom: 16 }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            marginBottom: 8,
                          }}
                        >
                          {guest.guestType === "CHILD"
                            ? `Child ${index - adultCount + 1}`
                            : `Adult ${index + 1}`}
                        </div>

                        <div className="ht-bm-form-grid ht-bm-guest-name-grid">
  <div className="ht-bm-field ht-bm-title-field">
    <label>Title *</label>

    <select
      value={guest.title || ""}
      onChange={(e) => {
        const next = [...guestDetails];

        next[index] = {
          ...next[index],
          title: e.target.value,
        };

        setGuestDetails(next);

        setErrors((prev) => ({
          ...prev,
          guests: "",
        }));
      }}
    >
      <option value="">Select</option>

      {guest.guestType === "ADULT" ? (
        <>
          <option value="Mr">Mr</option>
          <option value="Mrs">Mrs</option>
          <option value="Ms">Ms</option>
        </>
      ) : (
        <>
          <option value="Master">Master</option>
          <option value="Miss">Miss</option>
        </>
      )}
    </select>
  </div>

  <div className="ht-bm-field">
    <label>First Name *</label>

    <input
      value={guest.firstName}
      onChange={(e) => {
        const next = [...guestDetails];

        next[index] = {
          ...next[index],
          firstName: e.target.value,
        };

        setGuestDetails(next);

        setErrors((prev) => ({
          ...prev,
          guests: "",
        }));
      }}
    />
  </div>

  <div className="ht-bm-field">
    <label>Last Name *</label>

    <input
      value={guest.lastName}
      onChange={(e) => {
        const next = [...guestDetails];

        next[index] = {
          ...next[index],
          lastName: e.target.value,
        };

        setGuestDetails(next);

        setErrors((prev) => ({
          ...prev,
          guests: "",
        }));
      }}
    />
  </div>
</div>

                        {guest.guestType === "CHILD" && (
                          <div className="ht-bm-field">
                            <label>Child Age *</label>

                            <input
                              type="number"
                              min="0"
                              max="12"
                              value={guest.age}
                              onChange={(e) => {
                                const next = [...guestDetails];
                                next[index] = {
                                  ...next[index],
                                  age: e.target.value,
                                };
                                setGuestDetails(next);
                                setErrors((prev) => ({
                                  ...prev,
                                  guests: "",
                                }));
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                    {errors.guests && (
                      <div
                        className="ht-field-error"
                        style={{ marginBottom: 12 }}
                      >
                        {errors.guests}
                      </div>
                    )}

                    <div className="ht-bm-field">
                      <label>Email Address *</label>
                      <input type="email" placeholder="your@email.com" value={form.email} onChange={e => updateForm("email", e.target.value)} className={errors.email ? "has-error" : ""} />
                      {errors.email && <div className="ht-field-error">{errors.email}</div>}
                    </div>
                    <div className="ht-bm-field">
                      <label>Phone Number *</label>
                      <div className="ht-bm-phone-row">
                        <span className="ht-bm-phone-code">🇮🇳 +91</span>
                        <input type="tel" placeholder="10-digit mobile" value={form.phone} onChange={e => updateForm("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} className={errors.phone ? "has-error" : ""} style={{ flex: 1 }} />
                      </div>
                      {errors.phone && <div className="ht-field-error">{errors.phone}</div>}
                    </div>
                    <div className="ht-bm-field">
                      <label>Special Requests (optional)</label>
                      <textarea placeholder="Any specific requests?" value={form.specialReq} onChange={e => updateForm("specialReq", e.target.value)} rows={3} />
                      <button type="button" className="ht-bm-btn-back" onClick={() => setShowSpecialRequests(true)}>
                        🎉 Add Special Requests {Object.keys(specialRequests).length > 0 ? "· Added" : ""}
                      </button>
                      {showSpecialRequests && (
                        <SpecialRequestsModal
                          initial={specialRequests}
                          saving={savingSpecialRequests}
                          onClose={() => setShowSpecialRequests(false)}
                          onSave={saveSpecialRequests}
                        />
                      )}

                    </div>
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0d1b3e", marginBottom: 8 }}>
                        Meal Preferences (optional)
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {Object.entries(mealLabels).map(([key, label]) => {
                          const active = mealSelections[key];
                          const included = includedMeals.has(key);
                          const addOn = mealAddOnByKey[key];
                          return (
                            <button key={key} type="button" onClick={() => toggleMeal(key)} disabled={included}
                              style={{
                                padding: "8px 14px", borderRadius: 10,
                                border: active ? "2px solid #0f766e" : "1px solid #cbd5e1",
                                background: included ? "#f0fdf4" : active ? "#ecfdf5" : "#fff",
                                fontSize: 12.5, fontWeight: 600,
                                color: active ? "#0f766e" : "#334155",
                                cursor: included ? "default" : "pointer",
                                display: "flex", alignItems: "center", gap: 6,
                              }}
                            >
                              {active ? "✓ " : ""}{label}
                              {included && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#059669", background: "#dcfce7", padding: "2px 6px", borderRadius: 6 }}>
                                  Included
                                </span>
                              )}
                              {!included && addOn?.priced && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#92400e", background: "#fef3c7", padding: "2px 6px", borderRadius: 6 }}>
                                  +{fmt(addOn.amount)}
                                </span>
                              )}
                              {!included && active && addOn && !addOn.priced && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", background: "#f1f5f9", padding: "2px 6px", borderRadius: 6 }}>
                                  Pay at property
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ fontSize: 11, color: "#5e5e5e", marginTop: 6 }}>
                        Meals marked "Included" are already part of your selected rate at no extra cost.
                        Any others you tick are sent as a request only — subject to availability and additional charges payable at the property.
                      </div>
                    </div>
                    <div className="ht-bm-gst-toggle">
  <input
    type="checkbox"
    id="gst-cb"
    checked={gstEnabled}
    onChange={(event) => {
      const checked = event.target.checked;
      setGstEnabled(checked);
      if (!checked) {
        setGstDetails({ gstin: "", companyName: "", address: "" });
      }
      setErrors((current) => ({
        ...current,
        gstin: "",
        gstCompanyName: "",
        gstAddress: "",
      }));
    }}
  />
  <label htmlFor="gst-cb">Add GST details for business travel</label>
</div>

{gstEnabled && (
  <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
    <div className="ht-bm-field">
      <label>GSTIN *</label>
      <input
        type="text"
        maxLength={15}
        placeholder="22AAAAA0000A1Z5"
        value={gstDetails.gstin}
        onChange={(event) => {
          const value = event.target.value.toUpperCase().replace(/[^0-9A-Z]/g, "");
          setGstDetails((current) => ({ ...current, gstin: value }));
          setErrors((current) => ({ ...current, gstin: "" }));
        }}
      />
      {errors.gstin && <span style={{ color: "#b91c1c", fontSize: 11.5, fontWeight: 600 }}>{errors.gstin}</span>}
    </div>

    <div className="ht-bm-field">
      <label>Company legal name *</label>
      <input
        type="text"
        value={gstDetails.companyName}
        onChange={(event) => {
          setGstDetails((current) => ({ ...current, companyName: event.target.value }));
          setErrors((current) => ({ ...current, gstCompanyName: "" }));
        }}
      />
      {errors.gstCompanyName && <span style={{ color: "#b91c1c", fontSize: 11.5, fontWeight: 600 }}>{errors.gstCompanyName}</span>}
    </div>

    <div className="ht-bm-field">
      <label>Billing address *</label>
      <input
        type="text"
        value={gstDetails.address}
        onChange={(event) => {
          setGstDetails((current) => ({ ...current, address: event.target.value }));
          setErrors((current) => ({ ...current, gstAddress: "" }));
        }}
      />
      {errors.gstAddress && <span style={{ color: "#b91c1c", fontSize: 11.5, fontWeight: 600 }}>{errors.gstAddress}</span>}
    </div>
  </div>
)}

{paymentError && (
  <div
    className="ht-bm-payment-error"
    style={{ marginTop: 12 }}
  >
    ⚠️ {paymentError}
  </div>
)}
</div>
)}

{holdActionError && (
                  <div style={{
                    marginTop: 14, padding: "10px 14px", borderRadius: 10,
                    background: "#fff1f2", border: "1px solid #fecdd3",
                    fontSize: 12.5, color: "#b91c1c",
                  }}>
                    ⚠️ {holdActionError}
                  </div>
                )}

                {step === 3 && (
                  <div className="ht-bm-section">
                    <div className="ht-bm-step3-room-summary">
                      <div className="ht-bm-step3-room-info">
                        <span className="ht-bm-step3-room-label">Booking for</span>
                        <span className="ht-bm-step3-room-name">
                          {selectedRoomLines.length > 0
                            ? selectedRoomLines
                              .map(
                                (line) =>
                                  `${line.room.name || "Room"}${line.qty > 1 ? ` × ${line.qty}` : ""}`
                              )
                              .join(" | ")
                            : roomsLoaded
                              ? "No room selected"
                              : hotel.selectedRoom?.name || hotel.rateName || "Selected Room"}
                        </span>
                        <span className="ht-bm-step3-room-dates">
                          {bookingCheckIn} → {bookingCheckOut} · {nights} night{nights !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="ht-bm-step3-room-price">
  <span>{fmt(paymentTotal)}</span>
  <small>all taxes included</small>
</div>
                    </div>

                    {paymentError && (
                      <div className="ht-bm-payment-error">
                        ⚠️ {paymentError}
                      </div>
                    )}

                    {showPaymentCheckout && (
                      <EasyBuzzPayment
                        bookingData={{
                          hotelName: checkoutPayload?.hotelName || hotel.name,
                          hotelLocation: checkoutPayload?.location || hotel.location,
                          hotelStars: checkoutPayload?.stars || hotel.stars,
                          checkIn: checkoutPayload?.checkIn || bookingCheckIn,
                          nights: checkoutPayload?.nights || nights,
                          guestFirstName:
                            checkoutPayload?.guestFirstName ||
                            guestDetails[0]?.firstName ||
                            "",
                          guestEmail:
                            checkoutPayload?.guestEmail ||
                            form.email,
                          guestPhone:
                            checkoutPayload?.guestContactNumber ||
                            form.phone,                            bookingReference:
                              confirmedRef ||
                              checkoutPayload?.requestId ||
                              `HOTEL-${Date.now()}`,
                        }}
                        amount={paymentTotal}
                        token={token}
                        onSuccess={handlePaymentSuccess}
                        onFailure={handlePaymentFailure}
                        onClose={() => setShowPaymentCheckout(false)}
                        onAuthExpired={() => {
  expireSession?.();
  setPaymentError(
    "Your login session expired. Please log in again to continue payment."
  );
}}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="ht-bm-right">
                <div className="ht-bm-summary-card">
                  <div className="ht-bm-summary-title">Price Summary</div>
                  <div className="ht-bm-summary-hotel">
                    <img src={hotel.image} alt="" className="ht-bm-summary-img" />
                    <div>
                      <div className="ht-bm-summary-hname">{hotel.name}</div>
                      <div className="ht-bm-summary-hloc">{hotel.location}</div>
                      <div className="ht-bm-summary-hstars">{renderStars(hotel.stars)}</div>
                    </div>
                  </div>
                  <div className="ht-bm-summary-duration">
                    <div className="ht-bm-summary-dur-label">Stay Duration</div>
                    <div className="ht-bm-summary-dur-controls">
                      <button className="ht-bm-dur-btn sm" onClick={() => adjustNights(-1)} disabled={nights <= 1}>−</button>
                      <span className="ht-bm-summary-dur-val">{nights} Night{nights !== 1 ? "s" : ""}</span>
                      <button className="ht-bm-dur-btn sm" onClick={() => adjustNights(1)}>+</button>
                    </div>
                  </div>
                  <div className="ht-bm-price-breakdown">
                    <div className="ht-bm-price-row">
  <span>
    Room subtotal · {nights}N · {totalSelectedRooms}R
  </span>
  <span>{fmt(roomTotal)}</span>
</div>

{mealAddOns.total > 0 && (
  <div
    className="ht-bm-price-row"
    style={{ color: "#92400e", fontWeight: 600 }}
  >
    <span>Selected meal add-ons</span>
    <span>+{fmt(mealAddOns.total)}</span>
  </div>
)}

<div className="ht-bm-price-divider" />

<div className="ht-bm-price-total">
  <span>
    {step === 3 && Number(checkoutPayload?.totalAmount) > 0
      ? "Final payable amount"
      : "Estimated total"}
  </span>
  <span>{fmt(paymentTotal)}</span>
</div>

<div className="ht-bm-price-note">
  {step === 3 && Number(checkoutPayload?.totalAmount) > 0
    ? "Based on the confirmed booking quote."
    : "Estimate only. Admin fees and applicable GST are finalized in the server quote before payment."}
</div>
                  </div>
                  {hotel.discount && <div className="ht-bm-offer-badge">🎁 {hotel.discount}</div>}
                  <div className="ht-bm-trust-row">
                    <div className="ht-bm-trust-item"><span><CheckCircle size={16} /></span> Free Cancellation</div>
                    <div className="ht-bm-trust-item"><span><ShieldCheck size={16} /></span> Secure Payments</div>
                    <div className="ht-bm-trust-item"><span><MessageCircleMore size={16} /></span> 24/7 Support</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {!isConfirmingHold && step < 4 && (
          <div className="ht-bm-footer">
            <div className="ht-bm-footer-left">
              {step > 1 && <button className="ht-bm-btn-back" disabled={isConfirmingHold} onClick={() => setStep(s => s - 1)}>← Back</button>}
            </div>
            <div className="ht-bm-footer-right">
              <div className="ht-bm-footer-total">
  <span className="ht-bm-footer-nights">
    {nights}N · {totalSelectedRooms}R
  </span>
</div>

              {!showPaymentCheckout && step < 3 && (
  <button
    className="ht-bm-btn-primary"
    onClick={nextStep}
    disabled={isPayDisabled}
    style={{
      cursor: isPayDisabled ? "not-allowed" : "pointer",
      opacity: isPayDisabled ? 0.55 : 1,
    }}
  >
                  {step === 1
                    ? "Continue to Guest Details →"
                    : bookOnHold
                      ? (holdSubmitting ? "Holding your room…" : "Hold This Room →")
                      : processing
                        ? "Preparing secure checkout…"
                        : `Pay ${fmt(paymentTotal)} via EasyBuzz →`}
                </button>
              )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FILTER COMPONENTS ────────────────────────────────────────
function FilterSection({ title, onReset, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ht-filter-card">
      <div className="ht-filter-hd" onClick={() => setOpen(o => !o)}>
        <span className="ht-filter-title">{title}</span>
        <div className="ht-filter-hd-right">
          {onReset && <button type="button" className="ht-filter-reset" onClick={e => { e.stopPropagation(); onReset(); }}>Reset</button>}
          <span className={`ht-filter-chevron${open ? " ht-open" : ""}`}>▼</span>
        </div>
      </div>
      {open && <div className="ht-filter-body">{children}</div>}
    </div>
  );
}

function CheckItem({ label, count, checked, onChange }) {
  return (
    <div className={`ht-check-item${checked ? " ht-checked" : ""}`} onClick={onChange}>
      <div className="ht-check-item-left">
        <div className="ht-checkbox">{checked ? "✓" : ""}</div>
        <span className="ht-check-label">{label}</span>
      </div>
      {count != null && <span className="ht-check-count">({count.toLocaleString()})</span>}
    </div>
  );
}
function ImageGalleryModal({ hotel, onClose }) {
  const categories = Object.keys(hotel.imagesByCategory || {});
  const [activeCategory, setActiveCategory] = useState(categories[0] || "Photos");
  const images = hotel.imagesByCategory?.[activeCategory] || [];
  const [index, setIndex] = useState(0);

  useEffect(() => { setIndex(0); }, [activeCategory]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex(i => Math.min(images.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIndex(i => Math.max(0, i - 1));
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [images.length, onClose]);

  if (!images.length) return null;

  return (
    <div
      className="ht-gallery-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 10001, display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px" }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{hotel.name}</div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: "transparent", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}
        >
          ✕
        </button>
      </div>

      {categories.length > 1 && (
        <div style={{ display: "flex", gap: 8, padding: "0 20px 12px", flexWrap: "wrap" }}>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: "6px 14px",
                borderRadius: 20,
                border: cat === activeCategory ? "2px solid #10b981" : "1px solid rgba(255,255,255,0.3)",
                background: cat === activeCategory ? "#10b981" : "transparent",
                color: "#fff",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {cat} ({hotel.imagesByCategory[cat].length})
            </button>
          ))}
        </div>
      )}

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", padding: "0 60px" }}>
        {index > 0 && (
          <button
            type="button"
            onClick={() => setIndex(i => i - 1)}
            style={{ position: "absolute", left: 10, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, color: "#fff", cursor: "pointer" }}
          >
            <ChevronLeft size={22} />
          </button>
        )}
        <img
          src={images[index]}
          alt={`${hotel.name} ${activeCategory} ${index + 1}`}
          style={{ maxHeight: "70vh", maxWidth: "100%", borderRadius: 10, objectFit: "contain" }}
        />
        {index < images.length - 1 && (
          <button
            type="button"
            onClick={() => setIndex(i => i + 1)}
            style={{ position: "absolute", right: 10, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 44, height: 44, color: "#fff", cursor: "pointer" }}
          >
            <ChevronRight size={22} />
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, overflowX: "auto", padding: "12px 20px 20px" }}>
        {images.map((url, i) => (
          <img
            key={i}
            src={url}
            alt=""
            onClick={() => setIndex(i)}
            style={{
              width: 70, height: 50, objectFit: "cover", borderRadius: 6, cursor: "pointer",
              border: i === index ? "2px solid #10b981" : "2px solid transparent", opacity: i === index ? 1 : 0.6,
            }}
          />
        ))}
      </div>
    </div>
  );
}
// ─── HOTEL CARD BENEFITS ───────────────────────────────────────
const BENEFIT_RULES = [
  { test: (h) => h.freeCancellation === true, label: () => "Free Cancellation", icon: CheckCircle },

  // ── Tag-based (richer signal than amenities, when supplier provides it) ──
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("family")), label: () => "Family Friendly", icon: Users },
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("business")), label: () => "Business Ready", icon: BriefcaseBusiness },
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("couple") || t.toLowerCase().includes("romantic")), label: () => "Couples Favourite", icon: HeartHandshake },
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("pet")), label: () => "Pet Friendly", icon: null },
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("luxury")), label: () => "Luxury Stay", icon: null },
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("beach")), label: () => "Beachfront", icon: Waves },
  { test: (h) => h.tags?.some(t => t.toLowerCase().includes("view")), label: (h) => h.tags.find(t => t.toLowerCase().includes("view")), icon: Mountain },

  // ── Amenity-based (existing) ──
  { test: (h) => h.amenities?.some(a => a.includes("wifi")), label: () => "Free WiFi", icon: Wifi },
  { test: (h) => h.amenities?.some(a => a.includes("breakfast")), label: () => "Breakfast Available", icon: UtensilsCrossed },
  { test: (h) => h.amenities?.some(a => a.includes("pool")), label: () => "Swimming Pool", icon: Waves },
  { test: (h) => h.amenities?.some(a => a.includes("parking")), label: () => "Free Parking", icon: ParkingCircle },
  { test: (h) => h.amenities?.some(a => a.includes("gym") || a.includes("fitness")), label: () => "Gym Access", icon: Dumbbell },
  { test: (h) => h.amenities?.some(a => a.includes("spa")), label: () => "Spa On-site", icon: HeartHandshake },
  { test: (h) => h.amenities?.some(a => a.includes("restaurant")), label: () => "On-site Restaurant", icon: UtensilsCrossed },
  { test: (h) => h.amenities?.some(a => a.includes("bar")), label: () => "Bar", icon: Martini },

  // ── Fallback signals ──
  { test: (h) => Number(h.stars) >= 4, label: (h) => `${h.stars}★ Property`, icon: null },
  { test: (h) => Number(h.rating) >= 4.5, label: () => "Highly Rated", icon: null },
];

function getHotelBenefits(hotel, count = 2) {
  const out = [];
  for (const rule of BENEFIT_RULES) {
    if (out.length >= count) break;
    if (rule.test(hotel)) out.push({ label: rule.label(hotel), icon: rule.icon });
  }
  return out;
}

function HotelSkeletonCard() {
  return (
    <div className="ht-skel-card">
      <div className="ht-skel-media shimmer" />
      <div className="ht-skel-body">
        <div className="ht-skel-line shimmer" style={{ width: "70%", height: 16 }} />
        <div className="ht-skel-line shimmer" style={{ width: "40%", height: 12, marginTop: 8 }} />
        <div className="ht-skel-line shimmer" style={{ width: "90%", height: 12, marginTop: 14 }} />
        <div className="ht-skel-row" style={{ marginTop: 16 }}>
          <div className="ht-skel-chip shimmer" />
          <div className="ht-skel-chip shimmer" />
        </div>
        <div className="ht-skel-footer">
          <div className="ht-skel-line shimmer" style={{ width: 80, height: 22 }} />
          <div className="ht-skel-btn shimmer" />
        </div>
      </div>
    </div>
  );
}

function HotelCard({ hotel, onWishlist, wishlisted, onBook, onOpen }) {
  const navigate = useNavigate();
  const [showGallery, setShowGallery] = useState(false);
  const openHotelBooking = () => {
    if (typeof onOpen === "function") return onOpen(hotel);
    navigate(`/hotelbooking/${hotel.id}`);
  };

  const benefits = getHotelBenefits(hotel, 2);
  const hasDiscount = hotel.displayOriginalPrice > hotel.displayPrice;
  const showTaxLine = hotel.taxes > 0;
  const showRatingBadge = hotel.rating > 0;
  const showImageCount = hotel.totalImageCount > 1;
  const showNearest = !!hotel.nearestAttraction;
  const showTags = Array.isArray(hotel.tags) && hotel.tags.length > 0;
  const showFreeCancel = hotel.freeCancellation === true;

  return (
    <div
      className="ht-card-v2"
      role="button"
      tabIndex={0}
      onClick={openHotelBooking}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openHotelBooking(); } }}
    >
      <div className="ht-card-v2-media">
        {hotel.image ? (
          <img src={hotel.image} alt={hotel.name} loading="lazy" />
        ) : (
          <div className="ht-card-v2-noimg"><MapPin size={32} /></div>
        )}

        <div className="ht-card-v2-media-top">
          <span className="ht-card-v2-pill">{(hotel.location || "").toUpperCase()}</span>
          {hotel.sponsored && <span className="ht-card-v2-sponsored">↗ SPONSORED</span>}
        </div>

        <button
          type="button"
          className={`ht-card-v2-wish${wishlisted ? " active" : ""}`}
          onClick={(e) => { e.stopPropagation(); onWishlist(hotel); }}
          aria-label="Wishlist"
        >
          {wishlisted ? "♥" : "♡"}
        </button>

        {showImageCount && (
          <button
            type="button"
            className="ht-card-v2-photos"
            onClick={(e) => { e.stopPropagation(); setShowGallery(true); }}
          >
            📷 {hotel.totalImageCount}
          </button>
        )}

        {hotel.discount && (
          <div className="ht-card-v2-discount">
            {hotel.bankOffer && <span className="ht-card-v2-bank">BANK</span>}
            {hotel.discount}
          </div>
        )}
      </div>

      <div className="ht-card-v2-body">
        <div className="ht-card-v2-headline">
          <div className="ht-card-v2-name-wrap">
            <div className="ht-card-v2-name">{hotel.name}</div>
            {hotel.subtitle && <div className="ht-card-v2-subtitle">{hotel.subtitle}</div>}
            <div className="ht-card-v2-stars">{renderStars(hotel.stars)}</div>
          </div>

          {showRatingBadge && (
            <div className="ht-card-v2-rating">
              <div className={`ht-card-v2-rating-pill${hotel.rating >= 4 ? " good" : ""}`}>
                {hotel.ratingLabel && <span className="label">{hotel.ratingLabel}</span>}
                <span className="score">{hotel.rating}</span>
              </div>
              {hotel.ratingCount > 0 && (
                <div className="ht-card-v2-rating-count">
                  {hotel.ratingCount.toLocaleString("en-IN")} ratings
                </div>
              )}
            </div>
          )}
        </div>

        {hotel.locationDetail && <div className="ht-card-v2-loc">{hotel.locationDetail}</div>}

        {showNearest && (
          <div className="ht-card-v2-nearest">
            <MapPin size={12} />
            {hotel.nearestAttraction.distance} {hotel.nearestAttraction.unit} to {hotel.nearestAttraction.name}
          </div>
        )}

        {showTags && (
          <div className="ht-card-v2-tags">
            {hotel.tags.slice(0, 3).map((t) => <span key={t} className="ht-card-v2-tag">{t}</span>)}
          </div>
        )}

        <div className="ht-card-v2-chips">
          {showFreeCancel && (
            <span className="ht-card-v2-chip good">
              <CheckCircle size={12} /> Free Cancellation
            </span>
          )}
          {benefits.map(({ label, icon: Icon }) => (
            <span key={label} className="ht-card-v2-chip">
              {Icon && <Icon size={12} />} {label}
            </span>
          ))}
        </div>

        {hotel.highlight && <div className="ht-card-v2-highlight">{hotel.highlight}</div>}

        <div className="ht-card-v2-footer">
          <div className="ht-card-v2-price-block">
            {hotel.displayPrice > 0 && (
              <div className="ht-card-v2-price-row">
                {hasDiscount && <span className="ht-card-v2-orig">{fmt(hotel.displayOriginalPrice)}</span>}
                <span className="ht-card-v2-price">{fmtExact(hotel.displayPrice)}</span>
                <span className="ht-card-v2-unit">{hotel.priceUnitLabel}</span>
              </div>
            )}
            </div>

          <button
            type="button"
            className="ht-card-v2-book-btn"
            onClick={(e) => { e.stopPropagation(); onBook(hotel); }}
          >
            Book Now
          </button>
        </div>
      </div>

      {showGallery && <ImageGalleryModal hotel={hotel} onClose={() => setShowGallery(false)} />}
    </div>
  );
}

// Small helper — only used for the subtitle line, no backend call needed
const HT_COUNTRY_NAMES = {
  IN: "India", US: "United States", GB: "United Kingdom", AE: "United Arab Emirates",
  SG: "Singapore", TH: "Thailand", MY: "Malaysia", ID: "Indonesia", NP: "Nepal",
  LK: "Sri Lanka", MV: "Maldives", FR: "France", IT: "Italy", ES: "Spain",
  DE: "Germany", CH: "Switzerland", AU: "Australia", QA: "Qatar", SA: "Saudi Arabia",
};
const htCountryName = (code) => HT_COUNTRY_NAMES[code] || code || "";

// ─── AUTOSUGGEST DROPDOWN ─────────────────────────────────────
function LocationAutosuggest({ value, country, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState("");
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const getBestLocationSuggestion = () => pickBestLocationSuggestion(suggestions, typed);

  const handleInput = (e) => {
    const val = e.target.value;
    setTyped(val);
    onChange(val);
    clearTimeout(timerRef.current);
    if (val.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await hotelApi.autosuggest(val);
        // ✅ Handle every shape the supplier might return
        const list = Array.isArray(data)
          ? data
          : (
            data?.autoSuggestions ||
            data?.suggestions ||
            data?.data ||
            data?.results ||
            data?.hotels ||
            []
          );
        setSuggestions(list.slice(0, 8));
        setOpen(list.length > 0);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const handleSelect = (item) => {
    if (!item) { setEditing(false); return; }
    onSelect({
      locationId: item.locationId || item.location_id || item.id || item.code,
      city: item.name || item.cityName || item.city_name || item.label || value,
      country: item.country || item.countryCode || item.country_code || "IN",
      locationType: item.type || item.locationType || "",
    });
    setOpen(false);
    setEditing(false);
    setTyped("");
  };

  const startEditing = () => {
    setEditing(true);
    setTyped("");
    onChange("");
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // Close the edit/search mode on outside click
  useEffect(() => {
    if (!editing) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setEditing(false);
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [editing]);

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: 1 }}>
      {!editing && (
        <button
          type="button"
          className="ht-sb-dest-display"
          onClick={startEditing}
          aria-label="Destination"
        >
          <span className="ht-sb-dest-display-name">
            {value ? `${value}${country ? `, ${htCountryName(country)}` : ""}` : "Where are you going?"}
          </span>
          <span className="ht-sb-dest-display-sub">
            {value ? (htCountryName(country) || "Tap to change") : "City, property name or location"}
          </span>
        </button>
      )}

      {editing && (
        <input
          ref={inputRef}
          className="ht-sb-input"
          type="text"
          placeholder="City, area or hotel name"
          value={typed}
          onChange={handleInput}
          onBlur={() => {
            setTimeout(() => setOpen(false), 200);
            // If user typed without selecting, auto-pick the best non-State suggestion
            if (typed.trim() && suggestions.length > 0) {
              const best = getBestLocationSuggestion();
              if (best && isRelatedLocationSuggestion(best, typed)) {
                handleSelect(best);
              } else {
                console.warn("[hotel-search] Ignoring unrelated autosuggest selection", {
                  typed,
                  suggestion: best,
                });
                setEditing(false);
              }
            } else {
              setEditing(false);
            }
          }}
        />
      )}

      {editing && loading && (
        <div style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }}>
          <TravelLoader inline size={16} />
        </div>
      )}

      {editing && open && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "110%", left: 0, right: 0, background: "#fff",
          border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          zIndex: 9999, maxHeight: 260, overflowY: "auto",
        }}>
          {suggestions.map((s, i) => (
            <div key={i}
              style={{ padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #f3f4f6" }}
              onMouseDown={() => handleSelect(s)}
              onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <MapPin size={14} style={{ flexShrink: 0, color: "#6b7280" }} />
              <span style={{ fontSize: 14 }}>
                {s.name || s.cityName || s.city_name || s.label || s.locationId}
              </span>
              {s.countryName && <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: "auto" }}>{s.countryName}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ─── ROOM & GUEST SELECTOR ──────────────────────────────────────
function RoomGuestField({ rooms, adults, childrenCount, childAges = [], onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const total = adults + childrenCount;
  const maxChildren = Math.min(rooms, adults);
  const allAgesSet = childrenCount === 0 || (childAges.length === childrenCount && childAges.every(a => a !== null && a !== undefined && a !== ""));

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const update = (key, delta) => {
    if (key === "rooms") {
  const nextRooms = Math.max(1, Math.min(9, rooms + delta));
  const nextAdults = Math.max(adults, nextRooms);

  const nextChildren = Math.min(
    childrenCount,
    nextRooms,
    nextAdults
  );

  onChange({
    rooms: nextRooms,
    adults: nextAdults,
    childrenCount: nextChildren,
    childAges: childAges.slice(0, nextChildren),
  });
}
    if (key === "adults") {
  const nextAdults = Math.max(
    rooms,
    Math.min(20, adults + delta)
  );

  const nextChildren = Math.min(
    childrenCount,
    rooms,
    nextAdults
  );

  onChange({
    rooms,
    adults: nextAdults,
    childrenCount: nextChildren,
    childAges: childAges.slice(0, nextChildren),
  });
}
    if (key === "children") {
  const nextCount = Math.max(
    0,
    Math.min(maxChildren, childrenCount + delta)
  );

  let nextAges = childAges.slice(0, nextCount);

  while (nextAges.length < nextCount) {
    nextAges.push("");
  }

  onChange({
    rooms,
    adults,
    childrenCount: nextCount,
    childAges: nextAges,
  });
}
  }

  const setChildAge = (index, age) => {
    const nextAges = [...childAges];
    nextAges[index] = age;
    onChange({ rooms, adults, childrenCount, childAges: nextAges });
  };

  return (
    <div className="ht-sb-field sb-field-small" ref={wrapRef} style={{ position: "relative" }}>
      <label>Rooms &amp; Guests</label>
      <div
        className="ht-sb-field-inner ht-sb-field-guest"
        onClick={() => setOpen((o) => !o)}
        style={{ cursor: "pointer" }}
      >
        <span className="ht-sb-field-icon"><Bed size={18} /></span>
        <strong style={{ fontSize: 14, whiteSpace: "nowrap" }}>
          {rooms} Room{rooms !== 1 ? "s" : ""}, {adults} Adult{adults !== 1 ? "s" : ""}
          {childrenCount > 0
            ? `, ${childrenCount} Child${childrenCount !== 1 ? "ren" : ""}`
            : ""}
        </strong>
      </div>

      {open && (
        <div
          className="pax-selector-card"
          style={{
            position: "absolute", top: "calc(100% + 8px)", left: 0,
            zIndex: 10000, width: 300, background: "#fff", borderRadius: 12,
            boxShadow: "0 16px 40px rgba(13,27,62,0.18)", padding: 14, border: "1px solid #e2e8f0",
            maxHeight: "70vh", overflowY: "auto",
          }}
        >
          {[
            { key: "rooms", label: "Rooms", val: rooms, min: 1 },
            { key: "adults", label: "Adults", val: adults, min: rooms },
            { key: "children", label: "Children", val: childrenCount, min: 0 },
          ].map(({ key, label, val, min }) => (
            <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#0d1b3e" }}>{label}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  type="button" onClick={() => update(key, -1)} disabled={val <= min}
                  style={{ width: 26, height: 26, borderRadius: "50%", border: "1px solid #cbd5e1", background: "#fff", cursor: val <= min ? "not-allowed" : "pointer" }}
                >−</button>
                <span style={{ width: 18, textAlign: "center", fontWeight: 700 }}>{val}</span>
                <button
                  type="button" onClick={() => update(key, 1)}
                  style={{ width: 26, height: 26, borderRadius: "50%", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer" }}
                >+</button>
              </div>
            </div>
          ))}

          {childrenCount > 0 && (
            <div style={{ marginTop: 6, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0d1b3e", marginBottom: 8 }}>
                Child age{childrenCount > 1 ? "s" : ""} at check-in <span style={{ color: "#b91c1c" }}>*</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Array.from({ length: childrenCount }, (_, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: "#334155" }}>Child {i + 1}</span>
                    <select
                      value={childAges[i] ?? ""}
                      onChange={(e) => setChildAge(i, e.target.value)}
                      style={{
                        flex: 1, maxWidth: 140, padding: "6px 8px", borderRadius: 8,
                        border: childAges[i] === "" || childAges[i] == null ? "1.5px solid #fca5a5" : "1px solid #cbd5e1",
                        fontSize: 12.5, background: "#fff",
                      }}
                    >
                      <option value="" disabled>Select age</option>
                      {Array.from({ length: 13 }, (_, age) => (
                        <option key={age} value={age}>{age === 0 ? "Under 1 year" : `${age} year${age > 1 ? "s" : ""}`}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {!allAgesSet && (
                <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 6 }}>
                  Please select an age from 0 to 12 years for each child.
                </div>
              )}
            </div>
          )}

          <button
            type="button" onClick={() => allAgesSet && setOpen(false)}
            disabled={!allAgesSet}
            style={{
              width: "100%", marginTop: 10,
              background: allAgesSet ? "linear-gradient(135deg,#10b981,#047857)" : "#cbd5e1",
              color: "#fff", border: "none", borderRadius: 10, padding: "9px 0",
              fontWeight: 700, fontSize: 12.5, cursor: allAgesSet ? "pointer" : "not-allowed",
            }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function HotelPage() {
  usePageMeta(
  "Book Hotels Online | Budget to Luxury Hotels – MakeBeeTrip",
  "Search and book hotels across 2M+ properties worldwide. Filter by price, rating and amenities, and enjoy instant confirmation on MakeBeeTrip hotel bookings.",
  "hotel booking, book hotels online, cheap hotels, luxury hotels, hotel deals, resorts booking, budget hotels, hotel rooms, city hotels"
);
  const navigate = useNavigate();
  const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const [cityInput, setCityInput] = useState("");
  const [locationId, setLocationId] = useState("");
  const [locationType, setLocationType] = useState("");
  const [countryOfResidence, setCountryOfResidence] = useState("India");
  const [destinationCountry, setDestinationCountry] = useState("IN");
  const [checkIn, setCheckIn] = useState(addDays(todayStr(), 1));   // tomorrow
  const [checkOut, setCheckOut] = useState(addDays(todayStr(), 3));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [childAges, setChildAges] = useState([]); // one entry per child, 0–17
  const [rooms, setRooms] = useState(1);
  const [guestFormError, setGuestFormError] = useState("");

  const [hotels, setHotels] = useState([]);
  const [defaultHotels, setDefaultHotels] = useState([]);
  const [defaultLoading, setDefaultLoading] = useState(true);
  const [defaultError, setDefaultError] = useState(null);
  const [displayCount, setDisplayCount] = useState(20);
  const [searchKey, setSearchKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [bookingHotel, setBookingHotel] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [pendingHotel, setPendingHotel] = useState(null);
  const { user, token } = useAuth();
  const authToken = token || getStoredToken();

  const [activeSort, setActiveSort] = useState("popularity");
  const [suggested, setSuggested] = useState({});
  const [priceFilter, setPriceFilter] = useState({});
  const [priceMode, setPriceMode] = useState("perNight");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [starFilter, setStarFilter] = useState({});
  const [ratingFilter, setRatingFilter] = useState(null);
  const [propTypeFilter, setPropTypeFilter] = useState({});
  const [locationFilter, setLocationFilter] = useState({});
  const [amenityFilter, setAmenityFilter] = useState({});
  const [ruleFilter, setRuleFilter] = useState({});
  const [dealFilter, setDealFilter] = useState({});
  const [viewFilter, setViewFilter] = useState({});
  const [topLocationFilter, setTopLocationFilter] = useState({});
  const [chainFilter, setChainFilter] = useState({});
  const [tagFilter, setTagFilter] = useState({});
  const [wishlist, setWishlist] = useState({});
  const [showMorePropTypes, setShowMorePropTypes] = useState(false);
  const [showMoreAmenities, setShowMoreAmenities] = useState(false);
  const [wishlistIds, setWishlistIds] = useState({});
  const [urlSearchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const searchRequestIdRef = useRef("");

  useEffect(() => {
    const prefill =
      urlSearchParams.get("destination") ||
      urlSearchParams.get("city");

    const checkInParam = urlSearchParams.get("checkIn");
    const checkOutParam = urlSearchParams.get("checkOut");
    const roomsParam = urlSearchParams.get("rooms");
    const adultsParam = urlSearchParams.get("adults");
    const childrenParam = urlSearchParams.get("children");
    const childAgesParam = urlSearchParams.get("childAges");

    if (prefill) {
      setCityInput(prefill);
    }

    if (checkInParam) setCheckIn(checkInParam);
    if (checkOutParam) setCheckOut(checkOutParam);
    if (roomsParam) setRooms(Number(roomsParam) || 1);
    if (adultsParam) setAdults(Number(adultsParam) || 2);

    if (childrenParam !== null) {
      setChildren(Number(childrenParam) || 0);
    }

    if (childAgesParam) {
      try {
        const parsedAges = JSON.parse(childAgesParam);

        setChildAges(
          Array.isArray(parsedAges)
            ? parsedAges.map(Number)
            : []
        );
      } catch {
        setChildAges([]);
      }
    }

    if (!prefill) return;

    // Reset so a fresh search fires even if we're re-running for a new query string
    setHasSearched(false);

    (async () => {
      try {
        const data = await hotelApi.autosuggest(prefill);
        const list = extractSuggestions(data);
        const best = pickBestLocationSuggestion(list, prefill);
        if (!best) return;

        const lid = best.locationId || best.location_id || best.id || best.code;
        const city = best.name || best.cityName || best.city_name || best.label || prefill;
        const country = best.country || best.countryCode || best.country_code || "IN";

        setLocationId(lid);
        setCityInput(city);
        setCountryOfResidence("India");
        setDestinationCountry(country);
        setLocationType(best.type || best.locationType || best.location_type || "");
      } catch (err) {
        console.warn("Could not resolve destination for search:", err.message);
      }
    })();
    // Re-run whenever the URL's query string changes (e.g. searching again
    // from the navbar overlay while already on /hotels), not just on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams.toString()]);

  // Once locationId resolves from the prefill above, trigger the search automatically
  useEffect(() => {
    const prefill = urlSearchParams.get("destination") || urlSearchParams.get("city");
    if (prefill && locationId && !hasSearched) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, hasSearched]);

  useEffect(() => {
    if (!user || !authToken) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/wishlist?userId=${user.id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const items = await res.json();
        const idMap = {};
        const wishSet = {};
        items.forEach(item => {
          idMap[item.destination] = item.id;
          wishSet[item.destination] = true;
        });
        setWishlistIds(idMap);
        // Mark as wishlisted by destination name match (hotel.id isn't stored, so we match by name)
        setWishlist(prevWishlist => {
          const next = { ...prevWishlist };
          [...hotels, ...defaultHotels].forEach(h => {
            if (wishSet[h.name]) next[h.id] = true;
          });
          return next;
        });
      } catch { /* ignore */ }
    })();
  }, [user, authToken, hotels, defaultHotels]);

  const toggle = (setter, key) => setter(prev => ({ ...prev, [key]: !prev[key] }));
  const handleWishlist = useCallback(async (hotel) => {
    if (!user || !authToken) {
      setPendingHotel(hotel);
      setShowLoginModal(true);
      return;
    }

    const id = hotel.id;
    const isWishlisted = !!wishlist[id];

    setWishlist(prev => ({ ...prev, [id]: !isWishlisted })); // optimistic UI

    try {
      if (isWishlisted) {
        const rowId = wishlistIds[hotel.name];
        if (rowId) {
          await fetch(`${API_BASE}/wishlist/${rowId}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${authToken}` },
          });
          setWishlistIds(prev => { const next = { ...prev }; delete next[hotel.name]; return next; });
        }
      } else {
        const res = await fetch(`${API_BASE}/wishlist`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            userId: user.id,
            destination: hotel.name,
            country: hotel.location,
            image: hotel.image,
            price: hotel.price,
            duration: null,
          }),
        });
        const saved = await res.json();
        setWishlistIds(prev => ({ ...prev, [hotel.name]: saved.id }));
      }
    } catch (err) {
      setWishlist(prev => ({ ...prev, [id]: isWishlisted })); // revert on failure
      alert("Could not update wishlist. Please try again.");
    }
  }, [wishlist, wishlistIds, user, authToken]);

  const nights = useMemo(() => calcNights(checkIn, checkOut), [checkIn, checkOut]);
  const childAgesValid = useMemo(
    () =>
      children === 0 ||
      (childAges.length === children &&
        childAges.every((a) => a !== null && a !== undefined && a !== "")),
    [children, childAges]
  );
  const getEffectivePrice = useCallback(
  (h) => {
    const bookingTotal = Number(h.price) || 0;

    return priceMode === "total"
      ? bookingTotal
      : bookingTotal / (
          Math.max(1, Number(nights) || 1) *
          Math.max(1, Number(rooms) || 1)
        );
  },
  [priceMode, nights, rooms]
);
  const buildSearchMeta = (overrides = {}) => ({
    city: overrides.city ?? cityInput,
    locationId: overrides.locationId ?? locationId,
    locationType: overrides.locationType ?? locationType,
    checkIn,
    checkOut,
    rooms,
    adults,
    children,
    childAges,
    searchKey,
    occupancies: Array.from({ length: rooms }, (_, index) => {
      const baseAdults = Math.floor(adults / rooms);
      const extraAdults = index < adults % rooms ? 1 : 0;

      const baseChildren = Math.floor(children / rooms);
      const extraChildren = index < children % rooms ? 1 : 0;

      const childCount = baseChildren + extraChildren;

      const priorChildren =
        baseChildren * index +
        Math.min(index, children % rooms);

      return {
        adultCount: baseAdults + extraAdults,
        childCount,
        childAges: childAges
          .slice(priorChildren, priorChildren + childCount)
          .map(Number),
      };
    }),
    countryOfResidence: countryOfResidence || "India",
    destinationCountry: overrides.destinationCountry ?? destinationCountry,
    requestId:
      overrides.requestId ||
      searchRequestIdRef.current ||
      "",
    travelType: isIndiaCountry(overrides.destinationCountry ?? destinationCountry) ? 0 : 1,
  });
  const buildHotelSearchMeta = (hotel) => {
    const base = buildSearchMeta();
    return {
      ...base,
      locationId: hotel.locationId || base.locationId,
      checkIn: hotel.checkIn || base.checkIn,
      checkOut: hotel.checkOut || base.checkOut,
      countryOfResidence: hotel.countryOfResidence || base.countryOfResidence,
      destinationCountry: hotel.destinationCountry || base.destinationCountry,
      occupancies: hotel.occupancies?.length ? hotel.occupancies : base.occupancies,
      searchKey: hotel.searchKey || base.searchKey,
    };
  };
  const persistSearchMeta = (meta) => {
    try { sessionStorage.setItem("hotelSearchMeta", JSON.stringify(meta)); } catch { }
  };

  const loadTopHotels = async () => {
    const fallbackCities = ["Goa", "Mumbai", "Delhi", "Bengaluru", "Jaipur"];
    setDefaultLoading(true);
    setDefaultError(null);
    try {
      const results = [];
      for (const city of fallbackCities) {
        results.push(await (async () => {
          try {
            const auto = await hotelApi.autosuggest(city);
            const list = extractSuggestions(auto);
            const best = pickBestLocationSuggestion(list);
            if (!best) return [];
            const locationId = best.locationId || best.location_id || best.id || best.code;
            if (!locationId) return [];
            const searchResponse = await hotelApi.search({
              locationId,
              checkIn: addDays(todayStr(), 1),
              checkOut: addDays(todayStr(), 2),
              occupancies: [{ adultCount: 2, childCount: 0, childAges: [] }],
              countryOfResidence: "India",
              travelType: isIndiaCountry(best.country || best.countryCode || best.country_code || "IN") ? 0 : 1,
            });
            const rawHotels = extractHotels(searchResponse) || [];
            const sk = searchResponse?.searchKey || searchResponse?.search_key || searchResponse?.sessionId || "";
            return rawHotels.map(h => normaliseHotel({
              ...h,
              searchKey: sk,
              locationId,
              locationType: best.type || best.locationType || best.location_type || "",
              countryOfResidence: "India",
              destinationCountry: best.country || best.countryCode || best.country_code || "IN",
              checkIn: addDays(todayStr(), 1),
              checkOut: addDays(todayStr(), 2),
              occupancies: [{ adultCount: 2, childCount: 0, childAges: [] }],
            }));
          } catch (err) {
            console.warn("Top hotel fetch failed for", city, err.message || err);
            return [];
          }
        })());
        await new Promise(r => setTimeout(r, 400)); // stagger — don't burst the supplier
      }

      const allHotels = results.flat();
      const uniqueHotels = allHotels.reduce((acc, hotel) => {
        if (!hotel?.id) return acc;
        const existing = acc[hotel.id];
        if (!existing || hotel.rating > existing.rating || (hotel.rating === existing.rating && hotel.price < existing.price)) {
          acc[hotel.id] = hotel;
        }
        return acc;
      }, {});
      const sorted = Object.values(uniqueHotels).sort((a, b) => {
        if (b.rating !== a.rating) return b.rating - a.rating;
        if (b.ratingCount !== a.ratingCount) return b.ratingCount - a.ratingCount;
        return a.price - b.price;
      });
      setDefaultHotels(sorted.slice(0, 20));
      try {
        sessionStorage.setItem("topHotelsCache", JSON.stringify({ hotels: sorted.slice(0, 20), ts: Date.now() }));
      } catch { /* ignore quota errors */ }
      setDisplayCount(20);
    } catch (err) {
      console.error("Failed to load default hotels:", err);
      setDefaultError("Unable to load top hotel recommendations right now.");
    } finally {
      setDefaultLoading(false);
    }
  };

  useEffect(() => {
  try {
    const cached = sessionStorage.getItem("topHotelsCache");

    if (cached) {
      const { hotels: cachedHotels, ts } = JSON.parse(cached);

      if (Date.now() - ts < 5 * 60 * 1000) {
        setDefaultHotels(cachedHotels);
        setDisplayCount(20);
      }
    }
  } catch {
    // ignore bad cache
  } finally {
    setDefaultLoading(false);
  }
}, []);

  // ── SEARCH ────────────────────────────────────────────────────
  const doSearch = async () => {
    setGuestFormError("");
    if (!childAgesValid) {
      setGuestFormError(
        "Please select an age from 0 to 12 years for each child before searching."
      );
      return;
    }
    const flowRequestId =
      searchRequestIdRef.current ||
      generateRequestId();
    searchRequestIdRef.current = flowRequestId;

    let searchLocationId = locationId;
    let resolvedCountry = destinationCountry || "IN";
    let resolvedLocationType = locationType || "";
    let resolvedCity = cityInput;

    // If user typed but didn't pick from dropdown, fetch suggestions and auto-pick first
    if (!searchLocationId && cityInput.trim()) {
      try {
        const data = await hotelApi.autosuggest(cityInput.trim());
        const list = Array.isArray(data)
          ? data
          : (data?.autoSuggestions || data?.suggestions || data?.data || []);
        if (list.length > 0) {
          const first = pickBestLocationSuggestion(list, cityInput.trim());
          if (!first || !isRelatedLocationSuggestion(first, cityInput.trim())) {
            console.warn("[hotel-search] Autosuggest did not return a related location", {
              typed: cityInput.trim(),
              suggestion: first,
            });
            setError("Please select a matching location from the dropdown suggestions.");
            return;
          }
          searchLocationId = first.locationId || first.location_id || first.id || first.code;
          resolvedCity =
            first.name ||
            first.cityName ||
            first.label ||
            cityInput;
          resolvedCountry =
            first.country ||
            first.countryCode ||
            first.country_code ||
            "IN";
          resolvedLocationType =
            first.type ||
            first.locationType ||
            "";
          setLocationId(searchLocationId);
          setCountryOfResidence("India");
          setDestinationCountry(resolvedCountry);
          setLocationType(resolvedLocationType);
          setCityInput(resolvedCity);
        }
      } catch { /* ignore */ }
    }

    if (!searchLocationId) {
      setError("Please select a location from the dropdown suggestions.");
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    const meta = buildSearchMeta({
      locationId: searchLocationId,
      locationType: resolvedLocationType,
      city: resolvedCity,
      destinationCountry: resolvedCountry,
      requestId: flowRequestId,
    });
    persistSearchMeta(meta);

    try {
      const data = await hotelApi.search({
        locationId: searchLocationId,
        checkIn,
        checkOut,
        occupancies: meta.occupancies,
        countryOfResidence: meta.countryOfResidence,
        travelType: meta.travelType,
        requestId: flowRequestId,
      });

      // ✅ Log raw response so you can see the exact supplier shape
      console.log("🏨 RAW SUPPLIER RESPONSE keys:", Object.keys(data || {}));
      console.log("🏨 RAW SUPPLIER RESPONSE:", JSON.stringify(data, null, 2));

      // ✅ Try every key the supplier might use for the hotel list
      const rawList = extractHotels(data);
      const sk = extractSearchKey(data);

      setSearchKey(sk);

      const normalised = rawList.map(h => normaliseHotel({ ...h, searchKey: sk }));
      if (normalised.length > 0) {
        setHotels(normalised);
        setDisplayCount(20);
        persistSearchMeta({ ...meta, searchKey: sk });
      } else {
        setHotels([]);
        if (data?.meta?.status === "ERROR") {
          console.warn("🏨 Supplier search backend error (meta.status=ERROR):", data);
          setError("Our hotel partner couldn't process this search right now. Please try again in a moment.");
        } else {
          console.warn("🏨 Supplier returned 0 hotels:", data);
        }
      }

      setHasSearched(true);
    } catch (err) {
      console.error("[HotelPage] search error:", err);
      setError(err.message || "Search failed. Please try again.");
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenHotel = (hotel) => {
    const baseMeta = buildSearchMeta();
    const detailMeta = {
      ...baseMeta,
      searchKey: hotel.searchKey || baseMeta.searchKey || "",
      locationId: hotel.locationId || baseMeta.locationId,
      countryOfResidence: hotel.countryOfResidence || baseMeta.countryOfResidence,
      destinationCountry: hotel.destinationCountry || baseMeta.destinationCountry,
      checkIn: hotel.checkIn || baseMeta.checkIn,
      checkOut: hotel.checkOut || baseMeta.checkOut,
      occupancies:
  Array.isArray(hotel.occupancies) &&
  hotel.occupancies.length > 0
    ? hotel.occupancies
    : baseMeta.occupancies,
      locationType: hotel.locationType || baseMeta.locationType,
      city: hotel.city || hotel.location || baseMeta.city,
    };
    persistSearchMeta(detailMeta);

    const query = new URLSearchParams({
      searchKey: detailMeta.searchKey || "",
      checkIn: detailMeta.checkIn || addDays(todayStr(), 1),
      locationId: detailMeta.locationId || "",
      checkOut: detailMeta.checkOut || addDays(todayStr(), 2),
      hotelId: hotel.encodedHotelId || hotel.id || "",
      country: detailMeta.countryOfResidence || "India",
      occupancies: JSON.stringify(detailMeta.occupancies || [{ adultCount: 2, childCount: 0, childAges: [] }]),
      travelType: String(detailMeta.travelType ?? 1),
    });

    navigate(`/hotelbooking/${hotel.id}?${query.toString()}`);
  };

  const handleBookClick = (hotel) => {
    if (!childAgesValid) {
      setGuestFormError("Please select the age (under 17) for each child before booking.");
      return;
    }
    persistSearchMeta(buildSearchMeta());

    // Open instantly with the hotel data we already have.
    // BookingModal's own fetchRoomAvailability() refreshes the searchKey
    // and room list right after mount — no need to duplicate that call here.
    if (!user || !token) {
      setPendingHotel(hotel);
      setShowLoginModal(true);
    } else {
      setBookingHotel(hotel);
    }
  };
  const resetAll = () => {
    setSuggested({}); setPriceFilter({}); setBudgetMin(""); setBudgetMax("");
    setStarFilter({}); setRatingFilter(null); setPropTypeFilter({});
    setLocationFilter({}); setAmenityFilter({});
    setRuleFilter({}); setDealFilter({});
    setViewFilter({}); setTopLocationFilter({}); setTagFilter({});
    setChainFilter({});
  };
  const baseHotelsForCounts = hasSearched ? hotels : defaultHotels;

  const derivedFacets = useMemo(() => {
    const list = baseHotelsForCounts;
    const priceRanges = buildPriceRanges(list.map(getEffectivePrice))
      .map(r => ({ ...r, count: list.filter(h => getEffectivePrice(h) >= r.min && getEffectivePrice(h) < r.max).length }));
    const starCategory = [...new Set(list.map(h => h.stars).filter(s => s > 0))]
      .sort((a, b) => b - a)
      .map(s => ({ id: `s${s}`, label: `${s} Star`, stars: s, count: list.filter(h => h.stars === s).length }));
    const userRating = buildRatingTiers(list.map(h => h.rating))
      .map(r => ({ ...r, count: list.filter(h => h.rating >= r.min).length }));
    return {
      priceRanges,
      starCategory,
      userRating,
      propertyType: groupBySingleClient(list, h => h.propertyType, 10),
      amenities: groupByMultiClient(list, h => h.amenities, 20),
      houseRules: groupByMultiClient(list, h => h.houseRules, 20),
      tags: groupByMultiClient(list, h => h.tags, 20),
      offers: groupBySingleClient(list, h => h.discount || h.offer, 10),
      views: groupByMultiClient(list, h => h.views, 20),
      chains: groupBySingleClient(list, h => h.chain, 15),
      topLocations: groupBySingleClient(list, h => h.locationDetail, 10),
    };
  }, [baseHotelsForCounts, getEffectivePrice]);
  const filteredHotels = useMemo(() => {
    let list = [...(hasSearched ? hotels : defaultHotels)];

    // Suggested star filters — OR together, not chained AND
    const suggestedStars = [];
    if (suggested["5star"]) suggestedStars.push(5);
    if (suggested["4star"]) suggestedStars.push(4);
    if (suggested["3star"]) suggestedStars.push(3);
    if (suggestedStars.length > 0) {
      list = list.filter(h => suggestedStars.includes(h.stars));
    }

    if (suggested["last_minute"]) list = list.filter(h => h.price < 5000);
    if (suggested["breakfast"]) {
      list = list.filter(h =>
        h.tags?.some(t => t.toLowerCase().includes("breakfast")) ||
        h.amenities?.some(a => a.includes("breakfast")) ||
        h.highlight?.toLowerCase().includes("breakfast") ||
        h.rateName?.toLowerCase().includes("breakfast")
      );
    }


    // Budget min/max
    if (budgetMin !== "" || budgetMax !== "") {
      const mn = budgetMin !== "" ? Number(budgetMin) : 0;
      const mx = budgetMax !== "" ? Number(budgetMax) : Infinity;
      list = list.filter(h => getEffectivePrice(h) >= mn && getEffectivePrice(h) <= mx);
    }
    // Star filter
    const activeStars = Object.keys(starFilter).filter(k => starFilter[k]);
    if (activeStars.length > 0) {
      const nums = activeStars
        .map(k => derivedFacets.starCategory.find(s => s.id === k)?.stars)
        .filter(Boolean);
      list = list.filter(h => nums.includes(h.stars));
    }

    // Rating filter
    if (ratingFilter) {
      const r = derivedFacets.userRating.find(x => x.id === ratingFilter);
      if (r) list = list.filter(h => h.rating >= r.min);
    }

    // property type
    const activePropTypes = Object.keys(propTypeFilter).filter(k => propTypeFilter[k]);
    if (activePropTypes.length > 0) list = list.filter(h => activePropTypes.includes(h.propertyTypeId));

    // amenities
    const activeAmenities = Object.keys(amenityFilter).filter(k => amenityFilter[k]);
    if (activeAmenities.length > 0) list = list.filter(h => activeAmenities.every(id => h.amenityIds.includes(id)));

    // house rules
    const activeRules = Object.keys(ruleFilter).filter(k => ruleFilter[k]);
    if (activeRules.length > 0) list = list.filter(h => activeRules.every(id => h.houseRuleIds.includes(id)));

    // price range (bucket by whatever derivedFacets.priceRanges currently defines)
    const activePrices = Object.keys(priceFilter).filter(k => priceFilter[k]);
    if (activePrices.length > 0) {
      list = list.filter(h => {
        const bucket = derivedFacets.priceRanges.find(r => getEffectivePrice(h) >= r.min && getEffectivePrice(h) < r.max);
        return bucket && activePrices.includes(bucket.id);
      });
    }

    // tags ("Popular With Guests")
    const activeTags = Object.keys(tagFilter).filter(k => tagFilter[k]);
    if (activeTags.length > 0) list = list.filter(h => activeTags.some(id => h.tagIds.includes(id)));

    const activeOffers = Object.keys(dealFilter).filter(k => dealFilter[k]);
    if (activeOffers.length > 0) list = list.filter(h => activeOffers.includes(h.offerId));

    // room views
    const activeViews = Object.keys(viewFilter).filter(k => viewFilter[k]);
    if (activeViews.length > 0) list = list.filter(h => activeViews.every(id => h.viewIds.includes(id)));

    // top locations
    const activeTopLocations = Object.keys(topLocationFilter).filter(k => topLocationFilter[k]);
    if (activeTopLocations.length > 0) list = list.filter(h => activeTopLocations.includes(h.locationDetailId));

    // chains
    const activeChains = Object.keys(chainFilter).filter(k => chainFilter[k]);
    if (activeChains.length > 0) list = list.filter(h => activeChains.includes(h.chainId));

    // Deals filter


    // Deals filter
    if (dealFilter["lightning"]) {
      list = list.filter(h => h.discount?.toLowerCase().includes("lightning"));
    }
    if (dealFilter["muhurat"]) {
      list = list.filter(h => h.offer?.toLowerCase().includes("muhurat"));
    }

    // Sorting
    if (activeSort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (activeSort === "price_desc") list.sort((a, b) => b.price - a.price);
    else if (activeSort === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (activeSort === "best") list.sort((a, b) => (b.rating / (b.price || 1)) - (a.rating / (a.price || 1)));
    else list.sort((a, b) => (b.popularity || b.ratingCount || 0) - (a.popularity || a.ratingCount || 0));

    return list;
  }, [hotels, suggested, priceFilter, budgetMin, budgetMax, starFilter, ratingFilter,
    propTypeFilter, amenityFilter, ruleFilter, dealFilter, viewFilter, topLocationFilter, chainFilter, tagFilter, activeSort, derivedFacets, getEffectivePrice]);

  const activePills = useMemo(() => {
    const pills = [];
    Object.keys(suggested).filter(k => suggested[k]).forEach(k => {
      const f = suggestedFilters.find(x => x.id === k);
      if (f) pills.push({ key: `s_${k}`, label: f.label, remove: () => toggle(setSuggested, k) });
    });
    Object.keys(priceFilter).filter(k => priceFilter[k]).forEach(k => {
      const f = derivedFacets.priceRanges.find(x => x.id === k);
      if (f) pills.push({ key: `p_${k}`, label: f.label, remove: () => toggle(setPriceFilter, k) });
    });
    Object.keys(starFilter).filter(k => starFilter[k]).forEach(k => {
      const f = derivedFacets.starCategory.find(x => x.id === k);
      if (f) pills.push({ key: `st_${k}`, label: f.label, remove: () => toggle(setStarFilter, k) });
    });
    Object.keys(tagFilter).filter(k => tagFilter[k]).forEach(k => {
      const f = derivedFacets.tags.find(x => x.id === k);
      if (f) pills.push({ key: `tag_${k}`, label: f.label, remove: () => toggle(setTagFilter, k) });
    });
    if (ratingFilter) { const f = derivedFacets.userRating.find(x => x.id === ratingFilter); if (f) pills.push({ key: `r_${ratingFilter}`, label: f.label, remove: () => setRatingFilter(null) }); }
    return pills;
  }, [suggested, priceFilter, starFilter, ratingFilter, tagFilter, derivedFacets]);

  const sortedDefaultHotels = useMemo(() => {
    let list = [...defaultHotels];
    if (activeSort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (activeSort === "price_desc") list.sort((a, b) => b.price - a.price);
    else if (activeSort === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (activeSort === "best") list.sort((a, b) => (b.rating / (b.price || 1)) - (a.rating / (a.price || 1)));
    else list.sort((a, b) => (b.popularity || b.ratingCount || 0) - (a.popularity || a.ratingCount || 0)); // popularity
    return list;
  }, [defaultHotels, activeSort]);

  const resultHotels = filteredHotels;

const displayHotels = useMemo(
  () =>
    resultHotels.map((h) => {
      const roomCount = Math.max(1, Number(rooms) || 1);
      const nightCount = Math.max(1, Number(nights) || 1);

      // Prefer the API's final customer payable amount.
      // Do not use supplierBookingTotal as the display price.
      const apiPayableTotal = h.adminPricing?.payableTotal;

      const bookingTotal =
        apiPayableTotal != null &&
        Number.isFinite(Number(apiPayableTotal))
          ? Number(apiPayableTotal)
          : h.bookingTotal != null &&
              Number.isFinite(Number(h.bookingTotal))
            ? Number(h.bookingTotal)
            : Number(h.price) || 0;

      const divisor = roomCount * nightCount;

      return {
        ...h,

        displayPrice:
          priceMode === "total"
            ? bookingTotal
            : bookingTotal / divisor,

        displayOriginalPrice: 0,

        priceUnitLabel:
          priceMode === "total"
            ? ` · ${nightCount}N · ${roomCount}R`
            : " / room / night",

        customerPayableTotal: bookingTotal,
      };
    }),
  [resultHotels, priceMode, nights, rooms]
);
  const dynamicSuggestedCounts = useMemo(() => ({
    "5star": baseHotelsForCounts.filter(h => h.stars === 5).length,
    "4star": baseHotelsForCounts.filter(h => h.stars === 4).length,
    "3star": baseHotelsForCounts.filter(h => h.stars === 3).length,
    last_minute: baseHotelsForCounts.filter(h => h.price > 0 && h.price < 5000).length,
    breakfast: baseHotelsForCounts.filter(h =>
      h.tags?.some(t => t.toLowerCase().includes("breakfast")) ||
      h.amenities?.some(a => a.includes("breakfast")) ||
      h.highlight?.toLowerCase().includes("breakfast") ||
      h.rateName?.toLowerCase().includes("breakfast")
    ).length,
  }), [baseHotelsForCounts]);

  const resultTotal = hasSearched ? hotels.length : defaultHotels.length;
  const resultTitle = hasSearched
    ? `properties in ${cityInput}`
    : "top-ranked stays from our supplier network";

  return (
    <>
      {bookingHotel && (
        <BookingModal
          hotel={bookingHotel}
          searchMeta={buildHotelSearchMeta(bookingHotel)}
          onClose={() => setBookingHotel(null)}
          token={authToken}
          user={user}
        />
      )}
      {showLoginModal && (
        <Login
          onClose={() => setShowLoginModal(false)}
          onSwitchToSignup={() => { setShowLoginModal(false); setShowSignupModal(true); }}
          onLoginSuccess={() => {
            setShowLoginModal(false);
            if (pendingHotel) { setBookingHotel(pendingHotel); setPendingHotel(null); }
          }}
        />
      )}
      {showSignupModal && (
        <Signup
          onClose={() => setShowSignupModal(false)}
          onSwitchToLogin={() => { setShowSignupModal(false); setShowLoginModal(true); }}
        />
      )}

      <div className="ht-sb-outer">
        <div className="ht-sb-hero-copy"><h1>Where Luxury Meets Comfort</h1></div>
        <div className="ht-sb-row">
          <div className="ht-sb-field">
            <label>Destination</label>
            <div className="ht-sb-field-inner ht-sb-field-inner--dest">
              <span className="ht-sb-field-icon"><MapPin size={18} /></span>
              <div className="ht-sb-dest-wrap">
                <LocationAutosuggest
                  value={cityInput}
                  country={destinationCountry}
                  onChange={val => { setCityInput(val); setLocationId(""); }} // already doing this ✅
                  onSelect={({ locationId: lid, city, country, locationType: type }) => {
                    setLocationId(lid);
                    setCityInput(city);
                    setCountryOfResidence("India");
                    setDestinationCountry(country || "IN");
                    setLocationType(type || "");
                  }}
                />
              </div>
            </div>
            {locationId && (
              <div className="ht-sb-search-sub">
                {isIndiaCountry(destinationCountry)
                  ? "Domestic stay"
                  : "International stay"}
              </div>
            )}
          </div>

          <div className="ht-sb-field ht-sb-field-date-range">
            <div className="ht-sb-field-inner ht-sb-date-range-inner" style={{ gap: 10 }}>
              <HotelDateField
                label="CHECK-IN"
                value={checkIn}
                min={todayStr()}
                onChange={(value) => {
                  setCheckIn(value);
                  if (checkOut <= value) setCheckOut(addDays(value, 1));
                }}
              />
              <HotelDateField
                label="CHECK-OUT"
                value={checkOut}
                min={addDays(checkIn, 1)}
                onChange={(value) => setCheckOut(value)}
              />
            </div>
          </div>


          <RoomGuestField
            rooms={rooms}
            adults={adults}
            childrenCount={children}
            childAges={childAges}
            onChange={({ rooms: r, adults: a, childrenCount: c, childAges: ca }) => {
              setRooms(r); setAdults(a); setChildren(c); setChildAges(ca);
              setGuestFormError("");
            }}
          />

          <button type="button" className="ht-sb-search-btn" onClick={doSearch} disabled={loading || !childAgesValid}>      {loading
            ? <TravelLoader inline size={20} />
            : <span className="ht-sb-search-icon"><Search size={20} /></span>
          }
            <span className="ht-sb-search-main">{loading ? "Searching…" : "Search"}</span>
            <span className="ht-sb-search-sub">Find best stays</span>
          </button>
        </div>
        {guestFormError && (
          <div style={{ color: "#b91c1c", fontSize: 12.5, fontWeight: 600, padding: "8px 4px 0" }}>
            ⚠️ {guestFormError}
          </div>
        )}
        <div className="ht-sb-benefits">
          <span><ShieldCheck size={14} /> Best Rate Guarantee</span>
          <span><Clock3 size={14} /> Free Cancellation</span>
          <span><CreditCard size={14} /> Pay at Hotel</span>
          <span><Lock size={14} /> Secure Booking</span>
        </div>
      </div>

      <div className="ht-page-body">
        {showMobileFilters && (
          <div className="ht-filter-backdrop" onClick={() => setShowMobileFilters(false)} />
        )}
        <aside className={`ht-sidebar${showMobileFilters ? " ht-sidebar-open" : ""}`}>
          <div className="ht-mobile-filter-header">
            <span>Filters</span>
            <button type="button" onClick={() => setShowMobileFilters(false)}>✕</button>
          </div>
          <div className="ht-loc-search">
            <span className="ht-loc-search-icon"><Search size={18} /></span>
            <input type="text" placeholder="Search locality / hotel name" />
          </div>
          <FilterSection title="Suggested For You" onReset={() => setSuggested({})}>
            {suggestedFilters.filter(f => dynamicSuggestedCounts[f.id] > 0).map(f => (
              <CheckItem key={f.id} label={f.label} count={dynamicSuggestedCounts[f.id]}
                checked={!!suggested[f.id]} onChange={() => toggle(setSuggested, f.id)} />
            ))}
          </FilterSection>
          <FilterSection title="Preference" onReset={() => setPriceMode("perNight")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 17, padding: "8px 20px" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                <input
                  type="radio" name="ht-price-pref" checked={priceMode === "perNight"}
                  onChange={() => setPriceMode("perNight")} style={{ marginTop: 2, accentColor: "#10b981" }}
                />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0d1b3e" }}>Price per Night</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                <input
                  type="radio" name="ht-price-pref" checked={priceMode === "total"}
                  onChange={() => setPriceMode("total")} style={{ marginTop: 2, accentColor: "#10b981" }}
                />
                <span>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "#0d1b3e", display: "block" }}>Total Price</span>
                  <span style={{ fontSize: 11, color: "#5e5e5e" }}>All rooms and nights, including applicable taxes and fees</span>
                </span>
              </label>
            </div>
          </FilterSection>
          {derivedFacets.priceRanges.length > 0 && (
            <FilterSection title="Price Per Night" onReset={() => setPriceFilter({})}>
              {derivedFacets.priceRanges.map(r => (
                <CheckItem key={r.id} label={r.label} count={r.count}
                  checked={!!priceFilter[r.id]} onChange={() => toggle(setPriceFilter, r.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.starCategory.length > 0 && (
            <FilterSection title="Star Category" onReset={() => setStarFilter({})}>
              <div className="ht-star-row">
                {derivedFacets.starCategory.map(s => (
                  <div key={s.id} className={`ht-star-chip${starFilter[s.id] ? " active" : ""}`} onClick={() => toggle(setStarFilter, s.id)}>
                    ★ {s.stars} Star ({s.count})
                  </div>
                ))}
              </div>
            </FilterSection>
          )}

          {derivedFacets.userRating.length > 0 && (
            <FilterSection title="User Rating" onReset={() => setRatingFilter(null)}>
              {derivedFacets.userRating.map(r => (
                <CheckItem key={r.id} label={r.label} count={r.count}
                  checked={ratingFilter === r.id} onChange={() => setRatingFilter(p => p === r.id ? null : r.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.propertyType.length > 0 && (
            <FilterSection title="Property Type" onReset={() => setPropTypeFilter({})}>
              {derivedFacets.propertyType.map(p => (
                <CheckItem key={p.id} label={p.label} count={p.count}
                  checked={!!propTypeFilter[p.id]} onChange={() => toggle(setPropTypeFilter, p.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.amenities.length > 0 && (
            <FilterSection title="Amenities" onReset={() => setAmenityFilter({})} defaultOpen={false}>
              {derivedFacets.amenities.map(a => (
                <CheckItem key={a.id} label={a.label} count={a.count}
                  checked={!!amenityFilter[a.id]} onChange={() => toggle(setAmenityFilter, a.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.houseRules.length > 0 && (
            <FilterSection title="House Rules" onReset={() => setRuleFilter({})} defaultOpen={false}>
              {derivedFacets.houseRules.map(r => (
                <CheckItem key={r.id} label={r.label} count={r.count}
                  checked={!!ruleFilter[r.id]} onChange={() => toggle(setRuleFilter, r.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.tags.length > 0 && (
            <FilterSection title="Popular With Guests" onReset={() => setTagFilter({})}>
              {derivedFacets.tags.map(t => (
                <CheckItem key={t.id} label={t.label} count={t.count}
                  checked={!!tagFilter[t.id]} onChange={() => toggle(setTagFilter, t.id)} />
              ))}
            </FilterSection>
          )}
          {derivedFacets.offers.length > 0 && (
            <FilterSection title="Current Offers" onReset={() => setDealFilter({})} defaultOpen={false}>
              {derivedFacets.offers.map(o => (
                <CheckItem key={o.id} label={o.label} count={o.count}
                  checked={!!dealFilter[o.id]} onChange={() => toggle(setDealFilter, o.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.views.length > 0 && (
            <FilterSection title="Room Views" onReset={() => setViewFilter({})} defaultOpen={false}>
              {derivedFacets.views.map(v => (
                <CheckItem key={v.id} label={v.label} count={v.count}
                  checked={!!viewFilter[v.id]} onChange={() => toggle(setViewFilter, v.id)} />
              ))}
            </FilterSection>
          )}

          {derivedFacets.topLocations.length > 0 && (
            <FilterSection title="Top Locations" onReset={() => setTopLocationFilter({})} defaultOpen={false}>
              {derivedFacets.topLocations.map(l => (
                <CheckItem key={l.id} label={l.label} count={l.count}
                  checked={!!topLocationFilter[l.id]} onChange={() => toggle(setTopLocationFilter, l.id)} />
              ))}
            </FilterSection>
          )}
          {derivedFacets.chains.length > 0 && (
            <FilterSection title="Chains" onReset={() => setChainFilter({})} defaultOpen={false}>
              {derivedFacets.chains.map(c => (
                <CheckItem key={c.id} label={c.label} count={c.count}
                  checked={!!chainFilter[c.id]} onChange={() => toggle(setChainFilter, c.id)} />
              ))}
            </FilterSection>
          )}
        </aside>

        <main className="ht-results-panel">
          {activePills.length > 0 && (
            <div className="ht-pills-row">
              {activePills.map(pill => (
                <span key={pill.key} className="ht-pill">{pill.label}<span className="ht-pill-x" onClick={pill.remove}>×</span></span>
              ))}
              <button type="button" className="ht-clear-all" onClick={resetAll}>Clear all</button>
            </div>
          )}
          <div className="ht-results-toolbar">
            <button
              type="button"
              className="ht-mobile-filter-btn"
              onClick={() => setShowMobileFilters(true)}
            >
              <Search size={16} /> Filters
            </button>
            <div className="ht-sort-bar">
              <div className="ht-sort-list">
                {sortOptions.map(o => <div key={o.id} className={`ht-sort-opt${activeSort === o.id ? " active" : ""}`} onClick={() => setActiveSort(o.id)}>{o.label}</div>)}
              </div>
            </div>
          </div>
          {!hasSearched && defaultLoading && (
            <>
              <div className="ht-count-bar ht-count-bar--loading">
                <span className="ht-loading-pulse-dot" />
                Finding top-rated stays for you…
              </div>
              <div className="ht-hotel-grid">
                {Array.from({ length: 8 }).map((_, i) => <HotelSkeletonCard key={i} />)}
              </div>
            </>
          )}
          {!hasSearched && !defaultLoading && defaultError && (
            <div className="ht-empty-state">
              <AlertCircle size={40} color="#ef4444" />
              <div className="ht-empty-title" style={{ color: "#ef4444" }}>Could not load default hotels</div>
              <div className="ht-empty-sub">{defaultError}</div>
              <button className="ht-bm-btn-primary" style={{ marginTop: 16 }} onClick={loadTopHotels}>Retry</button>
            </div>
          )}
          {!hasSearched && !defaultLoading && !defaultError && defaultHotels.length > 0 && (
            <>
              <div className="ht-count-bar">
                Showing <strong>{resultHotels.length}</strong> of <strong>{resultTotal}</strong> {resultTitle}
              </div>
              <div className="ht-hotel-grid">
                {displayHotels.slice(0, displayCount).map(hotel => (
                  <HotelCard key={hotel.id} hotel={hotel} onWishlist={handleWishlist} wishlisted={!!wishlist[hotel.id]} onBook={handleBookClick} onOpen={handleOpenHotel} />
                ))}
              </div>
              {resultHotels.length > displayCount && (
                <div className="ht-show-more-row">
                  <button type="button" className="ht-bm-btn-primary" onClick={() => setDisplayCount(prev => Math.min(prev + 10, resultHotels.length))}>
                    Show {Math.min(10, resultHotels.length - displayCount)} more hotels
                  </button>
                </div>
              )}
            </>
          )}
          {!hasSearched && !defaultLoading && !defaultError && defaultHotels.length === 0 && (
            <div className="ht-empty-state">
              <div className="ht-empty-icon"><Search size={40} /></div>
              <div className="ht-empty-title">Search for hotels</div>
              <div className="ht-empty-sub">Enter a destination above and hit Search to find available hotels.</div>
            </div>
          )}
          {loading && (
            <>
              <div className="ht-count-bar ht-count-bar--loading">
                <span className="ht-loading-pulse-dot" />
                Searching available hotels…
              </div>
              <div className="ht-hotel-grid">
                {Array.from({ length: 8 }).map((_, i) => <HotelSkeletonCard key={`s${i}`} />)}
              </div>
            </>
          )}
          {error && !loading && (
            <div className="ht-empty-state">
              <AlertCircle size={40} color="#ef4444" />
              <div className="ht-empty-title" style={{ color: "#ef4444" }}>Search failed</div>
              <div className="ht-empty-sub">{error}</div>
              <button className="ht-bm-btn-primary" style={{ marginTop: 16 }} onClick={doSearch}>Try again</button>
            </div>
          )}
          {hasSearched && !loading && !error && (
            <>
              <div className="ht-count-bar">
                Showing <strong>{filteredHotels.length}</strong> of{" "}
                <strong>{hotels.length}</strong> matching properties
              </div>
              {filteredHotels.length === 0 ? (
                <div className="ht-empty-state">
                  <div className="ht-empty-icon"><Landmark size={20} /></div>
                  <div className="ht-empty-title">No hotels available</div>
                  <div className="ht-empty-sub">
                    The supplier returned no results for <strong>{cityInput}</strong> on these dates.
                    Try selecting a specific city (not state), choose future dates, or try a different destination.
                  </div>
                  <button className="ht-bm-btn-back" style={{ marginTop: 16 }} onClick={resetAll}>Clear all filters</button>
                </div>
              ) : (
                <>
                  <div className="ht-hotel-grid">
                    {displayHotels.slice(0, displayCount).map(hotel => (
                      <HotelCard key={hotel.id} hotel={hotel} onWishlist={handleWishlist} wishlisted={!!wishlist[hotel.id]} onBook={handleBookClick} onOpen={handleOpenHotel} />
                    ))}
                  </div>
                  {filteredHotels.length > displayCount && (
                    <div className="ht-show-more-row">
                      <button type="button" className="ht-bm-btn-primary" onClick={() => setDisplayCount(prev => Math.min(prev + 10, filteredHotels.length))}>
                        Show {Math.min(10, filteredHotels.length - displayCount)} more hotels
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
}
